/**
 * ShowBuilderApp — Multi-Story News Show Composer
 *
 * Steps:
 *   1. select-type  — Pick show format (Top 10, Fatafat, Breaking, etc.)
 *   2. configure    — Show name, resolution, FPS
 *   3. stories      — Add/edit stories (headline, ticker, breaking flag, etc.)
 *   4. edit         — 4-panel editor: story list | scene viewer | properties | timeline bar
 */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Player } from '@remotion/player';
import type { Story, Scene, ShowProject, ShowTemplateId, StoryAsset, SceneLayoutId, LayoutConfig, ScreenStyle, OverlayElementGeometry, OverlayGeometry, ContentLayerKey, ContentLayerTransform, ContentLayerTransforms, CustomLayer, CustomLayerType } from './types';
import { DEFAULT_OVERLAY_GEOMETRY, DEFAULT_CONTENT_LAYER_TRANSFORM } from './types';
import type { UploadedAsset } from '../types';
import { SceneComposition } from './components/SceneComposition';
import { IntroComposition } from './components/IntroComposition';
import type { IntroCompositionProps } from './components/IntroComposition';
import type { SceneProps } from './types';
import { generateAllScenes } from './engine/sceneRulesEngine';
import { buildShowTimeline } from './engine/showTimelineBuilder';
import { getShowTemplate } from './templates/showTemplates';
import { LAYOUT_LABELS } from './types';
import {
  loadLayoutConfigs, saveLayoutConfigs, upsertLayoutConfig, deleteLayoutConfig,
  DEFAULT_LAYOUT_CONFIGS,
} from './engine/layoutConfigStore';

// ─── Constants ────────────────────────────────────────────────

const SHOW_TYPES = [
  { id: 'top-10-news' as ShowTemplateId, label: 'Top 10 News', labelHi: 'टॉप 10 खबरें', icon: '🏆', maxStories: 10, color: '#ef4444' },
  { id: 'top-20-news' as ShowTemplateId, label: 'Top 20 News', labelHi: 'टॉप 20 खबरें', icon: '📋', maxStories: 20, color: '#f97316' },
  { id: 'fatafat-20' as ShowTemplateId, label: '20 Fatafat Khabrein', labelHi: '20 फटाफट खबरें', icon: '⚡', maxStories: 20, color: '#eab308' },
  { id: 'breaking-bulletin' as ShowTemplateId, label: 'Breaking News Bulletin', labelHi: 'ब्रेकिंग बुलेटिन', icon: '🔴', maxStories: null, color: '#dc2626' },
  { id: 'morning-bulletin' as ShowTemplateId, label: 'Morning Bulletin', labelHi: 'सुबह बुलेटिन', icon: '🌅', maxStories: null, color: '#6366f1' },
  { id: 'evening-bulletin' as ShowTemplateId, label: 'Evening Bulletin', labelHi: 'शाम बुलेटिन', icon: '🌆', maxStories: null, color: '#8b5cf6' },
  { id: 'business-news' as ShowTemplateId, label: 'Business Bulletin', labelHi: 'बिज़नेस बुलेटिन', icon: '📈', maxStories: null, color: '#10b981' },
  { id: 'sports-bulletin' as ShowTemplateId, label: 'Sports Bulletin', labelHi: 'स्पोर्ट्स बुलेटिन', icon: '🏟️', maxStories: null, color: '#3b82f6' },
  { id: 'custom-show' as ShowTemplateId, label: 'Custom Show', labelHi: 'कस्टम शो', icon: '⚙️', maxStories: null, color: '#64748b' },
] as const;

type ShowTypeItem = typeof SHOW_TYPES[number];
type BuilderStep = 'select-type' | 'configure' | 'stories' | 'edit';

// Minimal story form state (before converting to full Story object)
interface StoryForm {
  id: string;
  headline: string;
  subheadline: string;
  tickerText: string;
  isBreaking: boolean;
  script: string;
  bigNumber: string;
  enabled: boolean;
  // Media assets
  images: UploadedAsset[];        // up to 5 story images
  videos: UploadedAsset[];        // up to 2 video clips
  voiceover?: UploadedAsset;      // single audio voiceover file
}

function emptyStoryForm(id: string): StoryForm {
  return {
    id, headline: '', subheadline: '', tickerText: '',
    isBreaking: false, script: '', bigNumber: '', enabled: true,
    images: [], videos: [], voiceover: undefined,
  };
}

function formToStory(form: StoryForm, index: number): Story {
  // Build StoryAsset[] from uploaded media
  const assets: StoryAsset[] = [];
  form.images.forEach((img, i) => {
    assets.push({ id: crypto.randomUUID(), asset: img, role: i === 0 ? 'primary' : i === 1 ? 'secondary' : 'grid', order: i });
  });
  form.videos.forEach((vid, i) => {
    assets.push({ id: crypto.randomUUID(), asset: vid, role: 'video', order: i });
  });
  if (form.voiceover) {
    assets.push({ id: crypto.randomUUID(), asset: form.voiceover, role: 'audio', order: 0 });
  }

  return {
    id: form.id,
    index,
    enabled: form.enabled,
    isBreaking: form.isBreaking,
    title: form.headline || `Story ${index}`,
    headline: form.headline,
    subheadline: form.subheadline,
    tickerText: form.tickerText,
    script: form.script,
    bigNumber: form.bigNumber || undefined,
    location: '',
    reporterName: '',
    category: '',
    assets,
    sceneMode: 'auto',
    duration: 18,
    scenes: [],
  };
}

// ─── StepperInput ─────────────────────────────────────────────
// Defined at module level (outside ShowBuilderApp) so its reference is stable
// and React never unmounts/remounts it during re-renders.
// Maintains a local string draft while the user is typing; commits to the
// parent onChange only on blur or Enter.

interface StepperInputProps {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  onCapture?: () => void;
}

const StepperInput: React.FC<StepperInputProps> = ({ value, min, max, step, onChange, onCapture }) => {
  const [draft, setDraft] = React.useState<string>(String(value));
  const focusedRef = React.useRef(false);
  const clamp = (v: number) => Math.min(max, Math.max(min, parseFloat(v.toFixed(6))));

  // Sync from outside only when not focused (e.g. ▲/▼ button click)
  React.useEffect(() => {
    if (!focusedRef.current) setDraft(String(value));
  }, [value]);

  const commit = (raw: string) => {
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) {
      onChange(clamp(parsed));
    } else {
      setDraft(String(value)); // revert to last valid
    }
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={draft}
      onFocus={() => { focusedRef.current = true; onCapture?.(); }}
      onBlur={() => { focusedRef.current = false; commit(draft); }}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { commit(draft); (e.target as HTMLInputElement).blur(); }
        if (e.key === 'ArrowUp')   { e.preventDefault(); const next = clamp(value + step); onChange(next); setDraft(String(next)); }
        if (e.key === 'ArrowDown') { e.preventDefault(); const next = clamp(value - step); onChange(next); setDraft(String(next)); }
      }}
      style={{
        flex: 1, border: 'none', background: 'transparent',
        color: '#e2e8f0', fontSize: 11, textAlign: 'center',
        outline: 'none', padding: '4px 2px', minWidth: 0,
      }}
    />
  );
};

// ─── Root Component ───────────────────────────────────────────

export const ShowBuilderApp: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [step, setStep] = useState<BuilderStep>('select-type');
  const [selectedType, setSelectedType] = useState<ShowTypeItem | null>(null);
  const [showName, setShowName] = useState('');
  const [introPreset, setIntroPreset] = useState('buzz24-classic');
  const [bgMusic, setBgMusic] = useState<UploadedAsset | undefined>(undefined);
  const [bgMusicVolume, setBgMusicVolume] = useState(0.3);
  const [voiceoverVolume, setVoiceoverVolume] = useState(0.9);
  const [storyForms, setStoryForms] = useState<StoryForm[]>([emptyStoryForm(crypto.randomUUID())]);
  const [showProject, setShowProject] = useState<ShowProject | null>(null);
  const [generating, setGenerating] = useState(false);
  const [timelineResult, setTimelineResult] = useState<ReturnType<typeof buildShowTimeline> | null>(null);
  const [renderJobId, setRenderJobId] = useState<string | null>(null);
  const [showLayoutLibrary, setShowLayoutLibrary] = useState(false);
  const [layoutConfigs, setLayoutConfigs] = useState<LayoutConfig[]>(() => loadLayoutConfigs());

  const handleGenerate = useCallback(() => {
    if (!selectedType) return;
    setGenerating(true);

    setTimeout(() => {
      try {
        const showTemplate = getShowTemplate(selectedType.id);
        const stories: Story[] = storyForms
          .filter((f) => f.enabled && f.headline.trim())
          .map((f, i) => formToStory(f, i + 1));

        const populated = generateAllScenes(stories, showTemplate, {
          bgMusicUrl: bgMusic?.url,
          bgMusicVolume,
          voiceoverVolume,
          layoutConfigs,
        });

        const project: ShowProject = {
          id: crypto.randomUUID(),
          name: showName,
          showTemplateId: selectedType.id,
          showTemplate,
          outputSettings: { fps: 30, format: 'MP4', resolution: '1920x1080', codec: 'H264', bitrate: '8M' },
          language: 'Hindi',
          date: new Date().toISOString().slice(0, 10),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          stories: populated,
          bgMusic,
          introPreset,
          bgMusicVolume,
          voiceoverVolume,
        };

        const result = buildShowTimeline(project);
        project.masterTimeline = result.timeline;

        setShowProject(project);
        setTimelineResult(result);
        setStep('edit');
      } catch (e) {
        console.error('Generation failed:', e);
      } finally {
        setGenerating(false);
      }
    }, 100);
  }, [selectedType, storyForms, showName, bgMusic, introPreset, bgMusicVolume, voiceoverVolume]);

  return (
    <div style={{
      width: '100vw', height: '100vh',
      background: '#0a0f1e',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Inter', 'Roboto', sans-serif",
      color: '#fff', overflow: 'hidden',
    }}>
      {/* Top Bar */}
      <TopBar onBack={onBack} step={step} selectedType={selectedType} onLayoutLibrary={() => setShowLayoutLibrary(true)} />

      {/* Layout Library overlay */}
      {showLayoutLibrary && (
        <LayoutLibrary
          onClose={() => setShowLayoutLibrary(false)}
          colorPalette={showProject?.showTemplate.colorPalette ?? { primary:'#0A1628',secondary:'#1A3A6B',accent:'#E8C84A',background:'#0D1F3C',text:'#FFFFFF',ticker:'#0A1628',tickerText:'#FFFFFF',breakingBanner:'#CC0000',breakingText:'#FFFFFF' }}
          headlineFont={showProject?.showTemplate.headlineFont ?? { fontFamily:'Inter',fontSize:52,fontWeight:700,color:'#FFFFFF',letterSpacing:-0.5,lineHeight:1.2 }}
          subheadlineFont={showProject?.showTemplate.subheadlineFont ?? { fontFamily:'Inter',fontSize:28,fontWeight:400,color:'#CCCCCC',letterSpacing:0,lineHeight:1.4 }}
          layoutConfigs={layoutConfigs}
          onLayoutConfigsChange={(configs) => { setLayoutConfigs(configs); saveLayoutConfigs(configs); }}
        />
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {step === 'select-type' && (
          <SelectShowType onSelect={(t) => { setSelectedType(t); setShowName(t.label); setStep('configure'); }} />
        )}
        {step === 'configure' && selectedType && (
          <ConfigureShow
            showType={selectedType} showName={showName}
            onNameChange={setShowName}
            introPreset={introPreset} onIntroPresetChange={setIntroPreset}
            bgMusic={bgMusic} onBgMusicChange={setBgMusic}
            bgMusicVolume={bgMusicVolume} onBgMusicVolumeChange={setBgMusicVolume}
            voiceoverVolume={voiceoverVolume} onVoiceoverVolumeChange={setVoiceoverVolume}
            onBack={() => setStep('select-type')}
            onNext={() => setStep('stories')}
          />
        )}
        {step === 'stories' && selectedType && (
          <StoryManager
            showType={selectedType}
            stories={storyForms}
            onChange={setStoryForms}
            onBack={() => setStep('configure')}
            onGenerate={handleGenerate}
            generating={generating}
          />
        )}
        {step === 'edit' && showProject && timelineResult && selectedType && (
          <>
            <ShowEditor
              project={showProject}
              timeline={timelineResult}
              showType={selectedType}
              onBack={() => setStep('stories')}
              onUpdateProject={(updated) => {
                // Rebuild timeline whenever the project changes (scene durations, etc.)
                const fresh = buildShowTimeline(updated);
                updated.masterTimeline = fresh.timeline;
                setShowProject({ ...updated });
                setTimelineResult(fresh);
              }}
              onRender={(jobId) => setRenderJobId(jobId)}
            />
            {renderJobId && (
              <RenderOverlay
                jobId={renderJobId}
                onClose={() => setRenderJobId(null)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Top Bar ──────────────────────────────────────────────────

const STEPS: { id: BuilderStep; label: string }[] = [
  { id: 'select-type', label: 'Show Type' },
  { id: 'configure', label: 'Configure' },
  { id: 'stories', label: 'Stories' },
  { id: 'edit', label: 'Edit & Review' },
];

const TopBar: React.FC<{ onBack: () => void; step: BuilderStep; selectedType: ShowTypeItem | null; onLayoutLibrary?: () => void }> = ({ onBack, step, selectedType, onLayoutLibrary }) => {
  const currentIdx = STEPS.findIndex((s) => s.id === step);
  return (
    <div style={{
      height: 52, background: '#0d1117', borderBottom: '1px solid #1e293b',
      display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12, flexShrink: 0,
    }}>
      <button onClick={onBack} style={{ background: 'transparent', border: '1px solid #334155', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', color: '#94a3b8', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M7.5 2L3.5 6l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        Back
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: 6, background: 'linear-gradient(135deg,#cc0000,#ff3333)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>📺</div>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>Buzz24 <span style={{ color: '#8b5cf6' }}>Show Builder</span></span>
      </div>
      {selectedType && (
        <div style={{ fontSize: 11, color: selectedType.color, background: `rgba(${hexToRgb(selectedType.color)},0.15)`, padding: '3px 10px', borderRadius: 20, border: `1px solid ${selectedType.color}40`, fontWeight: 600 }}>
          {selectedType.icon} {selectedType.label}
        </div>
      )}
      <div style={{ flex: 1 }} />
      {onLayoutLibrary && (
        <button onClick={onLayoutLibrary} style={{ ...secondaryBtn, padding: '4px 12px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
          📐 Layout Library
        </button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div style={{ fontSize: 10, fontWeight: 600, color: i <= currentIdx ? '#e2e8f0' : '#334155', padding: '3px 8px', borderRadius: 4, background: i === currentIdx ? '#1e3a8a' : 'transparent' }}>
              {s.label}
            </div>
            {i < STEPS.length - 1 && <div style={{ width: 12, height: 1, background: '#1e293b' }} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ─── Step 1: Select Show Type ─────────────────────────────────

const SelectShowType: React.FC<{ onSelect: (t: ShowTypeItem) => void }> = ({ onSelect }) => (
  <div style={{ flex: 1, overflow: 'auto', padding: 32 }}>
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 6px' }}>Select Show Type</h1>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 28px' }}>Choose the format for your automated news bulletin</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 14 }}>
        {SHOW_TYPES.map((t) => <ShowTypeCard key={t.id} type={t} onSelect={onSelect} />)}
      </div>
    </div>
  </div>
);

const ShowTypeCard: React.FC<{ type: ShowTypeItem; onSelect: (t: ShowTypeItem) => void }> = ({ type, onSelect }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={() => onSelect(type)} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: hov ? `rgba(${hexToRgb(type.color)},0.1)` : '#0f172a', border: `1.5px solid ${hov ? type.color : '#1e293b'}`, borderRadius: 12, padding: '18px 20px', cursor: 'pointer', textAlign: 'left', color: '#fff', transition: 'all 0.15s', transform: hov ? 'translateY(-1px)' : 'none', boxShadow: hov ? `0 0 20px rgba(${hexToRgb(type.color)},0.2)` : 'none' }}>
      <div style={{ fontSize: 28, marginBottom: 10 }}>{type.icon}</div>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 3 }}>{type.label}</div>
      <div style={{ fontSize: 12, color: type.color, fontWeight: 600, marginBottom: 8 }}>{type.labelHi}</div>
      <div style={{ fontSize: 11, color: '#475569' }}>{type.maxStories ? `Up to ${type.maxStories} stories` : 'Unlimited stories'}</div>
    </button>
  );
};

// ─── Step 2: Configure ────────────────────────────────────────

const INTRO_PRESETS = [
  { id: 'buzz24-classic', label: 'Buzz24 Classic', desc: 'Logo zoom + red flash', emoji: '📺' },
  { id: 'slide-in',       label: 'Slide In',       desc: 'Channel name slides left', emoji: '➡️' },
  { id: 'flash',          label: 'Flash Break',    desc: 'Quick cuts & flash', emoji: '⚡' },
  { id: 'fade-up',        label: 'Fade Up',        desc: 'Elegant fade-in from bottom', emoji: '🌅' },
  { id: 'breaking',       label: 'Breaking Banner', desc: 'Red breaking news banner', emoji: '🔴' },
  { id: 'none',           label: 'No Intro',        desc: 'Start directly with stories', emoji: '▶️' },
];

const ConfigureShow: React.FC<{
  showType: ShowTypeItem; showName: string;
  onNameChange: (n: string) => void;
  introPreset: string; onIntroPresetChange: (v: string) => void;
  bgMusic?: UploadedAsset; onBgMusicChange: (a: UploadedAsset | undefined) => void;
  bgMusicVolume: number; onBgMusicVolumeChange: (v: number) => void;
  voiceoverVolume: number; onVoiceoverVolumeChange: (v: number) => void;
  onBack: () => void; onNext: () => void;
}> = ({ showType, showName, onNameChange, introPreset, onIntroPresetChange, bgMusic, onBgMusicChange, bgMusicVolume, onBgMusicVolumeChange, voiceoverVolume, onVoiceoverVolumeChange, onBack, onNext }) => (
  <div style={{ flex: 1, overflow: 'auto', padding: 32 }}>
    <div style={{ maxWidth: 620, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
        <span style={{ fontSize: 28 }}>{showType.icon}</span>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{showType.label}</h1>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{showType.labelHi}</p>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <Field label="Show Name">
          <input value={showName} onChange={(e) => onNameChange(e.target.value)} placeholder="e.g. Top 10 News — August 11" style={inputStyle} />
        </Field>
        <Field label="Language">
          <select style={inputStyle}><option>Hindi</option><option>English</option><option>Hindi + English</option></select>
        </Field>
        <Field label="Resolution">
          <select style={inputStyle}><option>1920 × 1080 (HD 16:9)</option><option>1080 × 1920 (Vertical 9:16)</option></select>
        </Field>
        <Field label="Frame Rate">
          <select style={inputStyle}><option>30 FPS</option><option>25 FPS</option></select>
        </Field>

        {/* Intro Preset */}
        <Field label="Intro Animation">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 4 }}>
            {INTRO_PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => onIntroPresetChange(p.id)}
                style={{
                  background: introPreset === p.id ? `rgba(${hexToRgb(showType.color)},0.15)` : '#0f172a',
                  border: `1.5px solid ${introPreset === p.id ? showType.color : '#1e293b'}`,
                  borderRadius: 8, padding: '10px 10px', cursor: 'pointer', textAlign: 'left', color: '#fff',
                  transition: 'all 0.12s',
                }}
              >
                <div style={{ fontSize: 18, marginBottom: 4 }}>{p.emoji}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: introPreset === p.id ? showType.color : '#94a3b8', marginBottom: 2 }}>{p.label}</div>
                <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.3 }}>{p.desc}</div>
              </button>
            ))}
          </div>
        </Field>

        {/* Background Music */}
        <Field label="Background Music">
          <MediaDropZone
            accept="audio/*"
            label={bgMusic ? bgMusic.name : 'Drop MP3 / WAV or click to browse'}
            icon="🎵"
            asset={bgMusic}
            onUpload={onBgMusicChange}
            onRemove={() => onBgMusicChange(undefined)}
            uploadKey="show-bgmusic"
            accentColor={showType.color}
          />
          {bgMusic && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: '#64748b', width: 90 }}>Music volume</span>
                <input type="range" min={0} max={1} step={0.05} value={bgMusicVolume}
                  onChange={(e) => onBgMusicVolumeChange(Number(e.target.value))}
                  style={{ flex: 1, accentColor: showType.color }} />
                <span style={{ fontSize: 11, color: '#64748b', width: 30 }}>{Math.round(bgMusicVolume * 100)}%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, color: '#64748b', width: 90 }}>VO volume</span>
                <input type="range" min={0} max={1} step={0.05} value={voiceoverVolume}
                  onChange={(e) => onVoiceoverVolumeChange(Number(e.target.value))}
                  style={{ flex: 1, accentColor: showType.color }} />
                <span style={{ fontSize: 11, color: '#64748b', width: 30 }}>{Math.round(voiceoverVolume * 100)}%</span>
              </div>
            </div>
          )}
        </Field>

        <div style={{ display: 'flex', gap: 10, paddingTop: 8 }}>
          <button onClick={onBack} style={secondaryBtn}>← Back</button>
          <button onClick={onNext} disabled={!showName.trim()} style={{ ...primaryBtn, background: showType.color, opacity: showName.trim() ? 1 : 0.5, cursor: showName.trim() ? 'pointer' : 'not-allowed' }}>
            Next: Add Stories →
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ─── Step 3: Story Manager ────────────────────────────────────

const StoryManager: React.FC<{
  showType: ShowTypeItem;
  stories: StoryForm[];
  onChange: (stories: StoryForm[]) => void;
  onBack: () => void;
  onGenerate: () => void;
  generating: boolean;
}> = ({ showType, stories, onChange, onBack, onGenerate, generating }) => {
  const [expandedId, setExpandedId] = useState<string | null>(stories[0]?.id ?? null);

  const addStory = () => {
    if (showType.maxStories && stories.length >= showType.maxStories) return;
    const newStory = emptyStoryForm(crypto.randomUUID());
    onChange([...stories, newStory]);
    setExpandedId(newStory.id);
  };

  const removeStory = (id: string) => {
    if (stories.length <= 1) return;
    onChange(stories.filter((s) => s.id !== id));
    if (expandedId === id) setExpandedId(stories.find((s) => s.id !== id)?.id ?? null);
  };

  const updateStory = (id: string, patch: Partial<StoryForm>) => {
    onChange(stories.map((s) => s.id === id ? { ...s, ...patch } : s));
  };

  const canGenerate = stories.some((s) => s.enabled && s.headline.trim());

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '14px 24px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 20 }}>{showType.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Add Stories</div>
          <div style={{ fontSize: 11, color: '#475569' }}>
            {stories.filter((s) => s.headline.trim()).length} ready
            {showType.maxStories ? ` / ${showType.maxStories} max` : ''}
          </div>
        </div>
        <button onClick={addStory} style={{ ...primaryBtn, background: showType.color, opacity: (showType.maxStories && stories.length >= showType.maxStories) ? 0.4 : 1 }}>
          + Add Story
        </button>
      </div>

      {/* Story list */}
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 760, margin: '0 auto' }}>
          {stories.map((story, i) => (
            <StoryRow
              key={story.id}
              story={story}
              index={i + 1}
              accentColor={showType.color}
              expanded={expandedId === story.id}
              onToggle={() => setExpandedId(expandedId === story.id ? null : story.id)}
              onChange={(patch) => updateStory(story.id, patch)}
              onRemove={() => removeStory(story.id)}
              canRemove={stories.length > 1}
            />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '14px 24px', borderTop: '1px solid #1e293b', display: 'flex', gap: 10, flexShrink: 0 }}>
        <button onClick={onBack} style={secondaryBtn}>← Back</button>
        <button
          onClick={onGenerate}
          disabled={!canGenerate || generating}
          style={{ ...primaryBtn, background: canGenerate ? showType.color : '#1e293b', opacity: canGenerate ? 1 : 0.5, cursor: canGenerate ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          {generating ? <Spinner /> : '✨'}
          {generating ? 'Generating scenes…' : 'Generate Show →'}
        </button>
      </div>
    </div>
  );
};

const StoryRow: React.FC<{
  story: StoryForm; index: number; accentColor: string;
  expanded: boolean; onToggle: () => void;
  onChange: (p: Partial<StoryForm>) => void;
  onRemove: () => void; canRemove: boolean;
}> = ({ story, index, accentColor, expanded, onToggle, onChange, onRemove, canRemove }) => {
  const [mediaTab, setMediaTab] = useState<'text' | 'media'>('text');
  return (
  <div style={{ background: '#0f172a', border: `1px solid ${expanded ? accentColor + '60' : '#1e293b'}`, borderRadius: 10, overflow: 'hidden', transition: 'border-color 0.15s' }}>
    {/* Row header */}
    <div
      onClick={onToggle}
      style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
    >
      {/* Number badge */}
      <div style={{ width: 30, height: 30, borderRadius: 7, flexShrink: 0, background: `rgba(${hexToRgb(accentColor)},0.15)`, border: `1px solid ${accentColor}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: accentColor }}>
        {String(index).padStart(2, '0')}
      </div>

      {/* Breaking badge */}
      {story.isBreaking && (
        <div style={{ background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4, letterSpacing: 1, textTransform: 'uppercase', flexShrink: 0 }}>
          LIVE
        </div>
      )}

      {/* Headline preview */}
      <div style={{ flex: 1, fontSize: 13, color: story.headline ? '#e2e8f0' : '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {story.headline || `Story ${index} — click to edit`}
      </div>

      {/* Expand arrow */}
      <div style={{ color: '#475569', fontSize: 10, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</div>
    </div>

    {/* Expanded form */}
    {expanded && (
      <div style={{ borderTop: '1px solid #1e293b' }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1e293b' }}>
          {(['text', 'media'] as const).map((tab) => (
            <button key={tab} onClick={() => setMediaTab(tab)} style={{
              flex: 1, padding: '9px 0', background: 'transparent', border: 'none',
              borderBottom: `2px solid ${mediaTab === tab ? accentColor : 'transparent'}`,
              color: mediaTab === tab ? accentColor : '#475569', fontSize: 11, fontWeight: 700,
              cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.5,
              transition: 'color 0.12s',
            }}>
              {tab === 'text' ? '✍️ Text' : `🖼 Media (${story.images.length + story.videos.length + (story.voiceover ? 1 : 0)})`}
            </button>
          ))}
        </div>

        {mediaTab === 'text' && (
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field label="Headline *">
              <input value={story.headline} onChange={(e) => onChange({ headline: e.target.value })} placeholder="Main news headline…" style={inputStyle} autoFocus />
            </Field>
            <Field label="Sub-headline">
              <input value={story.subheadline} onChange={(e) => onChange({ subheadline: e.target.value })} placeholder="Supporting detail (optional)" style={inputStyle} />
            </Field>
            <Field label="Ticker Text">
              <input value={story.tickerText} onChange={(e) => onChange({ tickerText: e.target.value })} placeholder="Bottom crawl text (optional)" style={inputStyle} />
            </Field>
            <Field label="Big Number / Stat">
              <input value={story.bigNumber} onChange={(e) => onChange({ bigNumber: e.target.value })} placeholder="e.g. ₹5,000 करोड़ (optional)" style={inputStyle} />
            </Field>
            <Field label="Script / Voiceover Text">
              <textarea value={story.script} onChange={(e) => onChange({ script: e.target.value })} placeholder="Voiceover script for this story…" rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
            </Field>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <div onClick={() => onChange({ isBreaking: !story.isBreaking })}
                  style={{ width: 36, height: 20, borderRadius: 10, background: story.isBreaking ? '#dc2626' : '#1e293b', border: '1px solid #334155', position: 'relative', transition: 'background 0.15s', cursor: 'pointer' }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: story.isBreaking ? 19 : 3, transition: 'left 0.15s' }} />
                </div>
                <span style={{ fontSize: 12, color: story.isBreaking ? '#f87171' : '#64748b', fontWeight: 600 }}>Breaking News</span>
              </label>
              <div style={{ flex: 1 }} />
              {canRemove && (
                <button onClick={onRemove} style={{ background: 'transparent', border: '1px solid #450a0a', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', color: '#f87171', fontSize: 11 }}>
                  Remove Story
                </button>
              )}
            </div>
          </div>
        )}

        {mediaTab === 'media' && (
          <StoryMediaUploader
            story={story}
            accentColor={accentColor}
            onChange={onChange}
          />
        )}
      </div>
    )}
  </div>
  );
};

// ─── Media Drop Zone ──────────────────────────────────────────

const MediaDropZone: React.FC<{
  accept: string;
  label: string;
  icon: string;
  asset?: UploadedAsset;
  onUpload: (a: UploadedAsset) => void;
  onRemove: () => void;
  uploadKey: string;
  accentColor: string;
  compact?: boolean;
}> = ({ accept, label, icon, asset, onUpload, onRemove, uploadKey, accentColor, compact }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const doUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('key', uploadKey);
      const res = await fetch('/api/upload', { method: 'POST', body: fd });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { message?: string; error?: { message?: string } };
        const msg = body.message ?? body.error?.message ?? `Upload failed (${res.status})`;
        throw new Error(msg);
      }
      const data = await res.json() as { asset: UploadedAsset };
      if (!data.asset) throw new Error('Server returned no asset');
      onUpload(data.asset);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Upload failed';
      console.error('[ShowBuilder] upload error:', msg);
      // Show visible error to user
      alert(`Upload failed: ${msg}\n\nMake sure the server is running (npm run server).`);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) doUpload(file);
  };

  const h = compact ? 60 : 72;

  return (
    <div style={{ position: 'relative' }}>
      <input ref={inputRef} type="file" accept={accept} style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = ''; }} />
      {asset ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: `rgba(${hexToRgb(accentColor)},0.08)`, border: `1px solid ${accentColor}40`, borderRadius: 8 }}>
          {/* Thumbnail for images */}
          {asset.type === 'image' && (
            <img src={asset.url} alt="" style={{ width: 40, height: 30, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
          )}
          {asset.type === 'video' && <span style={{ fontSize: 20, flexShrink: 0 }}>🎬</span>}
          {asset.type === 'audio' && <span style={{ fontSize: 20, flexShrink: 0 }}>🎵</span>}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.name}</div>
            <div style={{ fontSize: 10, color: '#475569' }}>{(asset.size / 1024).toFixed(0)} KB · {asset.type}</div>
          </div>
          <button onClick={onRemove} style={{ background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 14, padding: 4, flexShrink: 0 }}>✕</button>
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          style={{
            height: h, border: `1.5px dashed ${dragging ? accentColor : '#1e293b'}`,
            borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            cursor: uploading ? 'wait' : 'pointer',
            background: dragging ? `rgba(${hexToRgb(accentColor)},0.05)` : '#0a0f1e',
            transition: 'all 0.12s',
          }}
        >
          {uploading ? (
            <><Spinner /><span style={{ fontSize: 11, color: '#64748b' }}>Uploading…</span></>
          ) : (
            <><span style={{ fontSize: 16 }}>{icon}</span><span style={{ fontSize: 11, color: '#475569' }}>{label}</span></>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Story Media Uploader ─────────────────────────────────────

const StoryMediaUploader: React.FC<{
  story: StoryForm;
  accentColor: string;
  onChange: (p: Partial<StoryForm>) => void;
}> = ({ story, accentColor, onChange }) => {
  const addImage = (asset: UploadedAsset) => {
    if (story.images.length >= 5) return;
    onChange({ images: [...story.images, asset] });
  };
  const removeImage = (idx: number) => {
    onChange({ images: story.images.filter((_, i) => i !== idx) });
  };
  const addVideo = (asset: UploadedAsset) => {
    if (story.videos.length >= 2) return;
    onChange({ videos: [...story.videos, asset] });
  };
  const removeVideo = (idx: number) => {
    onChange({ videos: story.videos.filter((_, i) => i !== idx) });
  };

  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* Images */}
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
          Images ({story.images.length}/5)
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {story.images.map((img, i) => (
            <div key={img.url + i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }}>
              <img src={img.url} alt="" style={{ width: 44, height: 32, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontSize: 11, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.name}</div>
                <div style={{ fontSize: 10, color: '#334155' }}>{i === 0 ? 'Main image' : i === 1 ? 'Secondary' : `Grid ${i - 1}`}</div>
              </div>
              <button onClick={() => removeImage(i)} style={{ background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 13, padding: 4 }}>✕</button>
            </div>
          ))}
          {story.images.length < 5 && (
            <MediaDropZone
              accept="image/*"
              label={`Add image ${story.images.length + 1} of 5 — drop JPG/PNG/WebP`}
              icon="🖼"
              onUpload={addImage}
              onRemove={() => {}}
              uploadKey={`story-image-${story.id}-${story.images.length}`}
              accentColor={accentColor}
              compact
            />
          )}
        </div>
      </div>

      {/* Videos */}
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
          Video Clips ({story.videos.length}/2)
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {story.videos.map((vid, i) => (
            <div key={vid.url + i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>🎬</span>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontSize: 11, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vid.name}</div>
                <div style={{ fontSize: 10, color: '#334155' }}>{(vid.size / 1024 / 1024).toFixed(1)} MB · Video clip {i + 1}</div>
              </div>
              <button onClick={() => removeVideo(i)} style={{ background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 13, padding: 4 }}>✕</button>
            </div>
          ))}
          {story.videos.length < 2 && (
            <MediaDropZone
              accept="video/*"
              label="Add video clip — drop MP4/MOV"
              icon="🎬"
              onUpload={addVideo}
              onRemove={() => {}}
              uploadKey={`story-video-${story.id}-${story.videos.length}`}
              accentColor={accentColor}
              compact
            />
          )}
        </div>
      </div>

      {/* Voiceover audio */}
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
          Voiceover Audio
        </div>
        <MediaDropZone
          accept="audio/*"
          label={story.voiceover ? story.voiceover.name : 'Drop MP3 / WAV voiceover file'}
          icon="🎙"
          asset={story.voiceover}
          onUpload={(a) => onChange({ voiceover: a })}
          onRemove={() => onChange({ voiceover: undefined })}
          uploadKey={`story-vo-${story.id}`}
          accentColor={accentColor}
          compact
        />
      </div>

    </div>
  );
};

// ─── Step 4: Show Editor (Edit + Preview mode) ───────────────

type EditorMode = 'edit' | 'preview';

const ShowEditor: React.FC<{
  project: ShowProject;
  timeline: ReturnType<typeof buildShowTimeline>;
  showType: ShowTypeItem;
  onBack: () => void;
  onUpdateProject: (p: ShowProject) => void;
  onRender: (jobId: string) => void;
}> = ({ project, timeline, showType, onBack, onUpdateProject, onRender }) => {
  const [mode, setMode] = useState<EditorMode>('edit');
  const [rightTab, setRightTab] = useState<'scene' | 'settings'>('scene');
  const [starting, setStarting] = useState(false);

  // '__intro__' / '__outro__' are special IDs for intro/outro
  const [selectedStoryId, setSelectedStoryId] = useState<string>('__intro__');
  const [selectedSceneId, setSelectedSceneId] = useState<string>('');

  const isIntroSelected = selectedStoryId === '__intro__';
  const isOutroSelected = selectedStoryId === '__outro__';
  const isStorySelected = !isIntroSelected && !isOutroSelected;

  const selectedStory = isStorySelected
    ? (project.stories.find((s) => s.id === selectedStoryId) ?? null)
    : null;
  const selectedScene = selectedStory?.scenes.find((sc) => sc.id === selectedSceneId) ?? null;

  const handleUpdateScene = useCallback((updatedScene: Scene) => {
    onUpdateProject({
      ...project,
      stories: project.stories.map((s) => ({
        ...s,
        scenes: s.scenes.map((sc) => sc.id === updatedScene.id ? updatedScene : sc),
      })),
    });
  }, [project, onUpdateProject]);

  // Flatten all scenes in order for preview navigation
  const allScenes = useMemo(() =>
    project.stories.flatMap((s) => s.scenes.map((sc) => ({ ...sc, story: s }))),
    [project.stories]
  );

  const selectedSceneIdx = allScenes.findIndex((s) => s.id === selectedSceneId);

  const selectScene = (sceneId: string, storyId: string) => {
    setSelectedSceneId(sceneId);
    setSelectedStoryId(storyId);
  };

  const handleRender = async () => {
    setStarting(true);
    try {
      // Always rebuild timeline from latest scene durations before render
      const fresh = buildShowTimeline(project);
      const projectToRender = { ...project, masterTimeline: fresh.timeline };

      const res = await fetch('/api/show-render', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectToRender),
      });
      const data = await res.json() as { jobId: string };
      if (data.jobId) onRender(data.jobId);
    } catch (e) {
      console.error('Render start failed:', e);
    } finally {
      setStarting(false);
    }
  };

  // Shared intro props for Player
  const introPlayerProps: IntroCompositionProps = useMemo(() => ({
    preset: project.introPreset,
    channelName: project.name || 'Buzz24',
    colorPalette: project.showTemplate.colorPalette,
    tagline: 'News Channel',
    isOutro: false,
  }), [project.introPreset, project.name, project.showTemplate.colorPalette]);

  const outroPlayerProps: IntroCompositionProps = useMemo(() => ({
    ...introPlayerProps,
    isOutro: true,
  }), [introPlayerProps]);

  const fps = project.outputSettings?.fps ?? 30;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Mode toggle bar */}
      <div style={{
        height: 44, background: '#0d1117', borderBottom: '1px solid #1e293b',
        display: 'flex', alignItems: 'center', padding: '0 16px', gap: 8, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', background: '#0a0f1e', border: '1px solid #1e293b', borderRadius: 8, padding: 3, gap: 2 }}>
          {(['edit', 'preview'] as EditorMode[]).map((m) => (
            <button key={m} onClick={() => setMode(m)} style={{
              padding: '5px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
              background: mode === m ? (m === 'preview' ? '#7c3aed' : showType.color) : 'transparent',
              color: mode === m ? '#fff' : '#475569',
              transition: 'all 0.15s',
            }}>
              {m === 'edit' ? '✏️ Edit' : '▶ Preview'}
            </button>
          ))}
        </div>

        <div style={{ fontSize: 11, color: '#334155' }}>
          {isIntroSelected ? 'Intro · ' + project.introPreset
            : isOutroSelected ? 'Outro'
            : mode === 'preview' ? `Scene ${selectedSceneIdx + 1} / ${allScenes.length}`
            : `${timeline.totalDurationSec.toFixed(1)}s · ${project.stories.length} stories · ${allScenes.length} scenes`
          }
        </div>

        <div style={{ flex: 1 }} />

        <button
          onClick={handleRender}
          disabled={starting}
          style={{ ...primaryBtn, background: showType.color, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px', opacity: starting ? 0.7 : 1 }}
        >
          {starting ? <Spinner /> : '🎬'}
          {starting ? 'Starting…' : 'Render & Export'}
        </button>
      </div>

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left: INTRO → Stories → OUTRO */}
        <div style={{ width: 220, background: '#0a0f1e', borderRight: '1px solid #1e293b', overflow: 'auto', flexShrink: 0 }}>

          {/* INTRO entry */}
          <SpecialSceneRow
            label="INTRO"
            sublabel={project.introPreset}
            icon="🎬"
            selected={isIntroSelected}
            color="#6366f1"
            onClick={() => setSelectedStoryId('__intro__')}
          />

          {/* Stories */}
          {project.stories.map((story) => (
            <div key={story.id}>
              <div
                onClick={() => { setSelectedStoryId(story.id); if (story.scenes[0]) setSelectedSceneId(story.scenes[0].id); }}
                style={{
                  padding: '9px 12px', cursor: 'pointer',
                  background: story.id === selectedStoryId ? `rgba(${hexToRgb(showType.color)},0.1)` : '#0a0f1e',
                  borderLeft: story.id === selectedStoryId ? `3px solid ${showType.color}` : '3px solid transparent',
                  borderBottom: '1px solid #0f172a',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 9, fontWeight: 800, color: showType.color, background: `rgba(${hexToRgb(showType.color)},0.15)`, padding: '1px 5px', borderRadius: 3 }}>
                    #{String(story.index).padStart(2,'0')}
                  </span>
                  {story.isBreaking && <span style={{ fontSize: 8, background: '#dc2626', color: '#fff', padding: '1px 4px', borderRadius: 3, fontWeight: 800 }}>LIVE</span>}
                </div>
                <div style={{ fontSize: 11, color: story.id === selectedStoryId ? '#e2e8f0' : '#94a3b8', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {story.headline || 'Untitled'}
                </div>
              </div>
              {story.id === selectedStoryId && story.scenes.map((sc, i) => (
                <div
                  key={sc.id}
                  onClick={() => selectScene(sc.id, story.id)}
                  style={{
                    padding: '6px 12px 6px 28px', cursor: 'pointer', fontSize: 10,
                    background: sc.id === selectedSceneId ? '#0f172a' : 'transparent',
                    borderLeft: sc.id === selectedSceneId ? `3px solid #7c3aed` : '3px solid transparent',
                    color: sc.id === selectedSceneId ? '#c4b5fd' : '#475569',
                    borderBottom: '1px solid #0a0f1e',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <span style={{ fontSize: 9, background: sc.id === selectedSceneId ? '#7c3aed' : '#1e293b', color: '#fff', padding: '1px 4px', borderRadius: 3, fontWeight: 700 }}>
                    {String(i + 1).padStart(2,'0')}
                  </span>
                  {sc.layoutId} · {sc.duration}s
                </div>
              ))}
            </div>
          ))}

          {/* OUTRO entry */}
          <SpecialSceneRow
            label="OUTRO"
            sublabel="Thank you screen"
            icon="🏁"
            selected={isOutroSelected}
            color="#6366f1"
            onClick={() => setSelectedStoryId('__outro__')}
          />
        </div>

        {/* Center: Edit view OR Preview player */}
        <div style={{ flex: 1, overflow: (mode === 'edit' && isStorySelected) ? 'auto' : 'hidden', background: '#060b14' }}>
          {(isIntroSelected || isOutroSelected) ? (
            // Intro / Outro view (same in edit + preview)
            <IntroOutroPanel
              project={project}
              isOutro={isOutroSelected}
              fps={fps}
              introProps={isOutroSelected ? outroPlayerProps : introPlayerProps}
              onUpdateProject={onUpdateProject}
              accentColor={showType.color}
            />
          ) : mode === 'edit' ? (
            <div style={{ padding: 24 }}>
              {selectedStory ? (
                <SceneViewer
                  story={selectedStory}
                  selectedSceneId={selectedSceneId}
                  onSelectScene={(id) => setSelectedSceneId(id)}
                  accentColor={showType.color}
                />
              ) : (
                <EmptyState text="Select a story to view its scenes" />
              )}
            </div>
          ) : (
            <ScenePreviewPanel
              allScenes={allScenes}
              selectedIdx={selectedSceneIdx < 0 ? 0 : selectedSceneIdx}
              onSelectIdx={(i) => {
                const sc = allScenes[i];
                if (sc) selectScene(sc.id, sc.story.id);
              }}
              fps={fps}
              globalOverlay={project.showTemplate.globalOverlay}
            />
          )}
        </div>

        {/* Right: Tabbed panel */}
        <div style={{ width: 290, background: '#0a0f1e', borderLeft: '1px solid #1e293b', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
            {(['scene', 'settings'] as const).map((t) => (
              <button key={t} onClick={() => setRightTab(t)} style={{
                flex: 1, padding: '9px 0', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                background: rightTab === t ? '#0f172a' : 'transparent',
                color: rightTab === t ? '#e2e8f0' : '#475569',
                borderBottom: rightTab === t ? `2px solid ${showType.color}` : '2px solid transparent',
                transition: 'all 0.15s',
              }}>
                {t === 'scene' ? '🎬 Scene' : '⚙️ Settings'}
              </button>
            ))}
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {rightTab === 'scene' ? (
              selectedScene ? (
                <ScenePropEditor
                  scene={selectedScene}
                  accentColor={showType.color}
                  onSave={handleUpdateScene}
                />
              ) : (
                <EmptyState text="Select a scene to edit" />
              )
            ) : (
              <ShowSettingsPanel
                project={project}
                onChange={onUpdateProject}
                accentColor={showType.color}
              />
            )}
          </div>
        </div>
      </div>

      {/* Bottom: Master Timeline Bar */}
      <MasterTimeline
        project={project}
        timeline={timeline}
        selectedStoryId={selectedStoryId}
        onSelectStory={(id) => { setSelectedStoryId(id); const s = project.stories.find((x) => x.id === id); if (s?.scenes[0]) setSelectedSceneId(s.scenes[0].id); }}
        showType={showType}
        onBack={onBack}
      />
    </div>
  );
};

// ─── Scene Preview Panel (Remotion Player) ────────────────────

const ScenePreviewPanel: React.FC<{
  allScenes: Array<{ id: string; layoutId: string; duration: number; props: SceneProps; story: Story }>;
  selectedIdx: number;
  onSelectIdx: (i: number) => void;
  fps: number;
  globalOverlay?: ShowProject['showTemplate']['globalOverlay'];
}> = ({ allScenes, selectedIdx, onSelectIdx, fps, globalOverlay }) => {
  const bgAudioRef = useRef<HTMLAudioElement | null>(null);

  // Show-level bgMusic — find from first scene that has it
  const bgMusicUrl = useMemo(() => allScenes.find((s) => s.props.bgMusicUrl)?.props.bgMusicUrl, [allScenes]);
  const bgMusicVolume = allScenes[0]?.props.bgMusicVolume ?? 0.3;

  // Create/destroy native audio element for bgMusic (persists across scene changes)
  useEffect(() => {
    if (!bgMusicUrl) return;
    const audio = new Audio(bgMusicUrl);
    audio.loop = true;
    audio.volume = bgMusicVolume;
    audio.play().catch(() => { /* autoplay blocked — user must interact first */ });
    bgAudioRef.current = audio;
    return () => { audio.pause(); audio.src = ''; bgAudioRef.current = null; };
  }, [bgMusicUrl]); // only re-create if URL changes

  useEffect(() => {
    if (bgAudioRef.current) bgAudioRef.current.volume = bgMusicVolume;
  }, [bgMusicVolume]);

  const scene = allScenes[selectedIdx];
  if (!scene) return <EmptyState text="No scenes to preview — generate scenes first" />;

  const durationInFrames = Math.max(1, Math.round(scene.duration * fps));

  // Strip bgMusicUrl; override live overlay settings so Settings panel changes reflect immediately
  const playerProps: SceneProps = {
    ...scene.props,
    bgMusicUrl: undefined,
    _renderMode: 'preview',
    // Live overlay overrides — these mirror project.showTemplate.globalOverlay
    ...(globalOverlay ? {
      showTicker: globalOverlay.ticker,
      showLogo: globalOverlay.logo,
      showTopBar: globalOverlay.topBar,
      showStoryCounter: globalOverlay.storyCounter,
      tickerSpeed: globalOverlay.tickerSpeed,
    } : {}),
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Player area */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 24px 12px', background: '#030712' }}>
        <div style={{ width: '100%', maxWidth: 900 }}>
          {/* 16:9 aspect ratio wrapper */}
          <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', background: '#000', borderRadius: 8, overflow: 'hidden', boxShadow: '0 0 60px rgba(0,0,0,0.8)' }}>
            <div style={{ position: 'absolute', inset: 0 }}>
              <Player
                key={scene.id}
                component={SceneComposition as unknown as React.ComponentType<Record<string, unknown>>}
                inputProps={playerProps as unknown as Record<string, unknown>}
                durationInFrames={durationInFrames}
                compositionWidth={1920}
                compositionHeight={1080}
                fps={fps}
                style={{ width: '100%', height: '100%' }}
                controls
                autoPlay
                loop
                clickToPlay
              />
            </div>
          </div>

          {/* Scene info below player */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <span style={{ fontSize: 10, background: '#7c3aed', color: '#fff', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
              {scene.layoutId}
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {scene.props.headline}
            </span>
            <span style={{ fontSize: 10, color: '#475569' }}>{scene.duration}s · {durationInFrames}f</span>
          </div>
        </div>
      </div>

      {/* Scene strip (thumbnails navigation) */}
      <div style={{
        height: 80, background: '#0a0f1e', borderTop: '1px solid #1e293b',
        display: 'flex', alignItems: 'center', gap: 6, padding: '0 16px',
        overflowX: 'auto', flexShrink: 0,
      }}>
        {allScenes.map((sc, i) => (
          <div
            key={sc.id}
            onClick={() => onSelectIdx(i)}
            style={{
              height: 54, minWidth: 96, borderRadius: 6, cursor: 'pointer', flexShrink: 0,
              border: `2px solid ${i === selectedIdx ? '#7c3aed' : '#1e293b'}`,
              background: '#060b14', overflow: 'hidden', position: 'relative',
              transition: 'border-color 0.12s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2,
            }}
          >
            {/* Mini wireframe thumbnail */}
            <div style={{ fontSize: 9, fontWeight: 800, color: i === selectedIdx ? '#c4b5fd' : '#475569' }}>{sc.layoutId}</div>
            <div style={{ fontSize: 8, color: i === selectedIdx ? '#a78bfa' : '#334155', maxWidth: 84, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center', padding: '0 4px' }}>
              {sc.props.headline.slice(0, 20)}{sc.props.headline.length > 20 ? '…' : ''}
            </div>
            {i === selectedIdx && (
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: '#7c3aed' }} />
            )}
          </div>
        ))}
      </div>

      {/* Prev / Next controls */}
      <div style={{
        height: 40, background: '#0d1117', borderTop: '1px solid #1e293b',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexShrink: 0,
      }}>
        <button
          onClick={() => onSelectIdx(Math.max(0, selectedIdx - 1))}
          disabled={selectedIdx === 0}
          style={{ ...secondaryBtn, padding: '4px 14px', fontSize: 11, opacity: selectedIdx === 0 ? 0.3 : 1 }}
        >
          ← Prev
        </button>
        <span style={{ fontSize: 11, color: '#475569' }}>
          Scene {selectedIdx + 1} / {allScenes.length}
        </span>
        <button
          onClick={() => onSelectIdx(Math.min(allScenes.length - 1, selectedIdx + 1))}
          disabled={selectedIdx === allScenes.length - 1}
          style={{ ...secondaryBtn, padding: '4px 14px', fontSize: 11, opacity: selectedIdx === allScenes.length - 1 ? 0.3 : 1 }}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

// ─── Special Scene Row (Intro / Outro in left panel) ─────────

const SpecialSceneRow: React.FC<{
  label: string; sublabel: string; icon: string;
  selected: boolean; color: string; onClick: () => void;
}> = ({ label, sublabel, icon, selected, color, onClick }) => (
  <div
    onClick={onClick}
    style={{
      padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
      background: selected ? `rgba(99,102,241,0.12)` : '#0d1117',
      borderLeft: selected ? `3px solid ${color}` : '3px solid transparent',
      borderBottom: '1px solid #1e293b',
    }}
  >
    <div style={{
      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
      background: selected ? color : '#1e293b',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
    }}>{icon}</div>
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color: selected ? '#e2e8f0' : '#64748b', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 9, color: selected ? color : '#334155', marginTop: 2 }}>{sublabel}</div>
    </div>
  </div>
);

// ─── Intro / Outro Panel ──────────────────────────────────────

const IntroOutroPanel: React.FC<{
  project: ShowProject;
  isOutro: boolean;
  fps: number;
  introProps: IntroCompositionProps;
  onUpdateProject: (p: ShowProject) => void;
  accentColor: string;
}> = ({ project, isOutro, fps, introProps, onUpdateProject, accentColor }) => {
  const [channelName, setChannelName] = useState(project.name || 'Buzz24');
  const [tagline, setTagline] = useState('News Channel');
  const durationInFrames = 3 * fps; // 3-second intro preview

  // Rebuild props for live preview as user edits
  const liveProps: IntroCompositionProps = {
    ...introProps,
    channelName,
    tagline,
  };

  const handlePresetChange = (preset: string) => {
    onUpdateProject({ ...project, introPreset: preset });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Player */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 24px 12px', background: '#030712' }}>
        <div style={{ width: '100%', maxWidth: 900 }}>
          <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', background: '#000', borderRadius: 8, overflow: 'hidden', boxShadow: '0 0 60px rgba(0,0,0,0.8)' }}>
            <div style={{ position: 'absolute', inset: 0 }}>
              <Player
                key={`${project.introPreset}-${channelName}-${tagline}-${isOutro ? 'outro' : 'intro'}`}
                component={IntroComposition as unknown as React.ComponentType<Record<string, unknown>>}
                inputProps={liveProps as unknown as Record<string, unknown>}
                durationInFrames={durationInFrames}
                compositionWidth={1920}
                compositionHeight={1080}
                fps={fps}
                style={{ width: '100%', height: '100%' }}
                controls
                autoPlay
                loop
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <span style={{ fontSize: 10, background: '#6366f1', color: '#fff', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
              {isOutro ? 'OUTRO' : 'INTRO'}
            </span>
            <span style={{ fontSize: 11, color: '#64748b' }}>
              {isOutro ? 'Thank-you screen · ' : 'Show opener · '}{project.introPreset}
            </span>
          </div>
        </div>
      </div>

      {/* Edit controls (only for intro) */}
      {!isOutro && (
        <div style={{ background: '#0a0f1e', borderTop: '1px solid #1e293b', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Channel name & tagline */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 10, color: '#475569', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 5 }}>Channel Name</div>
              <input value={channelName} onChange={(e) => setChannelName(e.target.value)}
                style={{ ...inputStyle, fontSize: 12, padding: '7px 10px' }} />
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#475569', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 5 }}>Tagline</div>
              <input value={tagline} onChange={(e) => setTagline(e.target.value)}
                style={{ ...inputStyle, fontSize: 12, padding: '7px 10px' }} />
            </div>
          </div>

          {/* Preset picker */}
          <div>
            <div style={{ fontSize: 10, color: '#475569', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>Intro Animation</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
              {INTRO_PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handlePresetChange(p.id)}
                  style={{
                    background: project.introPreset === p.id ? `rgba(${hexToRgb(accentColor)},0.18)` : '#0f172a',
                    border: `1.5px solid ${project.introPreset === p.id ? accentColor : '#1e293b'}`,
                    borderRadius: 8, padding: '8px 8px', cursor: 'pointer', textAlign: 'left', color: '#fff',
                    transition: 'all 0.12s',
                  }}
                >
                  <div style={{ fontSize: 16, marginBottom: 3 }}>{p.emoji}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: project.introPreset === p.id ? accentColor : '#94a3b8' }}>{p.label}</div>
                  <div style={{ fontSize: 9, color: '#334155', lineHeight: 1.3 }}>{p.desc}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Layout Library (accessible from home screen) ─────────────

const SCREEN_STYLES: { id: ScreenStyle; label: string; icon: string }[] = [
  { id: 'full-screen', label: 'Full Screen',   icon: '⬛' },
  { id: 'split-lr',   label: 'Split L-R',      icon: '◧' },
  { id: 'split-tb',   label: 'Split T-B',      icon: '⬓' },
  { id: 'grid',       label: 'Grid',           icon: '⊞' },
  { id: 'text-focus', label: 'Text Focus',     icon: '𝐓' },
  { id: 'graphic',    label: 'Graphic',        icon: '✦' },
];

const LayoutLibrary: React.FC<{
  onClose: () => void;
  colorPalette: ShowProject['showTemplate']['colorPalette'];
  headlineFont: ShowProject['showTemplate']['headlineFont'];
  subheadlineFont: ShowProject['showTemplate']['subheadlineFont'];
  layoutConfigs: LayoutConfig[];
  onLayoutConfigsChange: (configs: LayoutConfig[]) => void;
}> = ({ onClose, colorPalette, headlineFont, subheadlineFont, layoutConfigs, onLayoutConfigsChange }) => {
  const [selectedId, setSelectedId] = useState<string>('L1');
  const [editConfig, setEditConfig] = useState<LayoutConfig | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedOverlay, setSelectedOverlay] = useState<keyof OverlayGeometry | null>(null);
  const [selectedLayer, setSelectedLayer] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState<'layers' | 'settings' | 'content'>('layers');
  const [customLayers, setCustomLayers] = useState<CustomLayer[]>([]);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const fps = 30;
  const durationInFrames = 5 * fps;

  // Preview assets — uploaded just for live testing in this editor
  const [prevAssets, setPrevAssets] = useState<{
    primaryImage?: UploadedAsset; secondaryImage?: UploadedAsset;
    video?: UploadedAsset; logo?: UploadedAsset;
    voiceover?: UploadedAsset; bgMusic?: UploadedAsset;
  }>({});

  // Preview text — editable for live testing
  const [prevText, setPrevText] = useState({
    headline:    'मुख्य समाचार: देश में नई आर्थिक नीति लागू',
    subheadline: 'वित्त मंत्री ने बजट में बड़े बदलाव की घोषणा की',
    bigNumber:   '₹5,000 Cr',
    quoteText:   '"यह नीति देश को आर्थिक मजबूती देगी"',
    tickerText:  'ताजा खबरें · Breaking News · Latest Updates',
    topBarText:  'BUZZ24 LIVE',
  });

  // Per-layer font/alignment overrides (for text layers in preview)
  const [textLayerStyle, setTextLayerStyle] = useState<Record<string, { fontSize?: number; align?: string }>>({});

  // Content layer position/scale/opacity transforms (edit mode — applied via SceneContext)
  const [contentLayerTransforms, setContentLayerTransforms] = useState<ContentLayerTransforms>({});

  // ── Undo / Redo ──────────────────────────────────────────────
  type HistorySnap = { editConfig: LayoutConfig; clt: ContentLayerTransforms };
  const [undoStack, setUndoStack] = useState<HistorySnap[]>([]);
  const [redoStack, setRedoStack] = useState<HistorySnap[]>([]);

  // Call BEFORE applying any change — saves current state so user can undo it
  const captureSnapshot = useCallback(() => {
    if (!editConfig) return;
    const snap: HistorySnap = { editConfig: { ...editConfig }, clt: { ...contentLayerTransforms } };
    setUndoStack((u) => [...u.slice(-49), snap]);
    setRedoStack([]); // new action clears redo branch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editConfig, contentLayerTransforms]);

  const handleUndo = useCallback(() => {
    setUndoStack((u) => {
      if (!u.length) return u;
      const prev = u[u.length - 1];
      setRedoStack((r) => {
        if (!editConfig) return r;
        return [...r.slice(-49), { editConfig: { ...editConfig }, clt: { ...contentLayerTransforms } }];
      });
      setEditConfig(prev.editConfig);
      setContentLayerTransforms(prev.clt);
      return u.slice(0, -1);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editConfig, contentLayerTransforms]);

  const handleRedo = useCallback(() => {
    setRedoStack((r) => {
      if (!r.length) return r;
      const next = r[r.length - 1];
      setUndoStack((u) => {
        if (!editConfig) return u;
        return [...u.slice(-49), { editConfig: { ...editConfig }, clt: { ...contentLayerTransforms } }];
      });
      setEditConfig(next.editConfig);
      setContentLayerTransforms(next.clt);
      return r.slice(0, -1);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editConfig, contentLayerTransforms]);

  // Keyboard shortcut: Cmd/Ctrl+Z → undo, Cmd/Ctrl+Shift+Z → redo
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      if ((e.key === 'z' && e.shiftKey) || e.key === 'y') { e.preventDefault(); handleRedo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleUndo, handleRedo]);

  // Arrow key nudge: ←→ = X, ↑↓ = Y, Shift = ×10 step
  // Uses a ref so the handler always sees fresh state without re-registering.
  const nudgeStateRef = useRef({ selectedLayer, contentLayerTransforms, editConfig });
  useEffect(() => { nudgeStateRef.current = { selectedLayer, contentLayerTransforms, editConfig }; });
  useEffect(() => {
    const CONTENT_KEY_MAP: Record<string, ContentLayerKey> = {
      primaryImage: 'primaryMedia', video: 'primaryMedia',
      secondaryImage: 'secondaryMedia',
      headline: 'headline', subheadline: 'subheadline',
      bigNumber: 'bigNumber', quoteText: 'quoteText',
    };
    const OVERLAY_KEYS: (keyof OverlayGeometry)[] = ['ticker', 'topBar', 'logo', 'storyCounter', 'breakingBanner'];

    const onArrow = (e: KeyboardEvent) => {
      const { selectedLayer: sl, contentLayerTransforms: clt, editConfig: ec } = nudgeStateRef.current;
      if (!sl || !ec) return;
      // Don't hijack arrow keys when user is typing inside an input or textarea
      const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
      e.preventDefault();

      const step = e.shiftKey ? 10 : 1;
      const dX = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
      const dY = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;

      const clKey = CONTENT_KEY_MAP[sl] as ContentLayerKey | undefined;
      const oKey = sl as keyof OverlayGeometry;

      if (clKey) {
        // Content layer — nudge transform dx/dy
        setContentLayerTransforms((prev) => {
          const cur = { ...DEFAULT_CONTENT_LAYER_TRANSFORM, ...(prev[clKey]) };
          return { ...prev, [clKey]: { ...cur, dx: cur.dx + dX, dy: cur.dy + dY } };
        });
      } else if (OVERLAY_KEYS.includes(oKey)) {
        // Overlay — nudge geo x/y
        setEditConfig((prev) => {
          if (!prev) return prev;
          const cur = { ...DEFAULT_OVERLAY_GEOMETRY[oKey], ...(prev.overlayGeometry?.[oKey]) };
          return {
            ...prev,
            overlayGeometry: {
              ...prev.overlayGeometry,
              [oKey]: { ...cur, x: Math.max(0, cur.x + dX), y: Math.max(0, cur.y + dY) },
            },
          };
        });
        setSelectedOverlay(oKey);
      }
    };
    window.addEventListener('keydown', onArrow);
    return () => window.removeEventListener('keydown', onArrow);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — nudgeStateRef always has fresh values

  // Wrapper: captures undo snapshot then updates editConfig (for discrete actions like toggles)
  const setEditConfigWithHistory = (cfg: LayoutConfig | ((prev: LayoutConfig) => LayoutConfig)) => {
    captureSnapshot();
    if (typeof cfg === 'function') {
      setEditConfig((prev) => prev ? cfg(prev) : prev);
    } else {
      setEditConfig(cfg);
    }
  };

  const getLayerTransform = (key: ContentLayerKey): ContentLayerTransform => ({
    ...DEFAULT_CONTENT_LAYER_TRANSFORM,
    ...(contentLayerTransforms[key]),
  });

  const updateLayerTransform = (key: ContentLayerKey, patch: Partial<ContentLayerTransform>) => {
    setContentLayerTransforms((prev) => ({
      ...prev,
      [key]: { ...DEFAULT_CONTENT_LAYER_TRANSFORM, ...(prev[key]), ...patch },
    }));
  };

  const resetLayerTransform = (key: ContentLayerKey) => {
    setContentLayerTransforms((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Maps screenStyle → best matching base layout ID
  const SCREEN_STYLE_BASE: Record<ScreenStyle, SceneLayoutId> = {
    'full-screen': 'L1',
    'split-lr':    'L2',
    'split-tb':    'L6',
    'grid':        'L10',
    'text-focus':  'L5',
    'graphic':     'L7',
  };

  // When selectedId changes, load that config into editConfig
  useEffect(() => {
    const found = layoutConfigs.find((c) => c.id === selectedId)
      ?? DEFAULT_LAYOUT_CONFIGS.find((c) => c.id === selectedId)
      ?? null;
    setEditConfig(found ? { ...found } : null);
    setSelectedOverlay(null);
  }, [selectedId, layoutConfigs]);

  // Helper: get effective geometry for an overlay key (editConfig override or default)
  const getGeo = (key: keyof OverlayGeometry): OverlayElementGeometry => ({
    ...DEFAULT_OVERLAY_GEOMETRY[key],
    ...(editConfig?.overlayGeometry?.[key]),
  });

  // Update a single geo field
  const updateGeo = (key: keyof OverlayGeometry, patch: Partial<OverlayElementGeometry>) => {
    if (!editConfig) return;
    setEditConfig({
      ...editConfig,
      overlayGeometry: {
        ...editConfig.overlayGeometry,
        [key]: { ...getGeo(key), ...patch },
      },
    });
  };

  // Full bounding boxes for each content layer per layout (1920×1080 logical space).
  // Used to render the drag/resize frames on the edit-mode canvas.
  const CONTENT_LAYER_BOUNDS: Partial<Record<ContentLayerKey, { x: number; y: number; w: number; h: number }>> = (() => {
    const lid = (editConfig?.baseLayoutId ?? editConfig?.id ?? 'L1') as SceneLayoutId;
    if (lid === 'L2') return {
      primaryMedia:   { x: 0,    y: 0,   w: 1056, h: 1080 },
      secondaryMedia: { x: 0,    y: 0,   w: 1056, h: 1080 },
      headline:       { x: 1096, y: 300, w: 784,  h: 200  },
      subheadline:    { x: 1096, y: 510, w: 784,  h: 130  },
    };
    if (lid === 'L3') return {
      primaryMedia:   { x: 864,  y: 0,   w: 1056, h: 1080 },
      secondaryMedia: { x: 864,  y: 0,   w: 1056, h: 1080 },
      headline:       { x: 80,   y: 300, w: 784,  h: 200  },
      subheadline:    { x: 80,   y: 510, w: 784,  h: 130  },
    };
    if (lid === 'L4') return {
      primaryMedia:   { x: 0,   y: 0,   w: 960,  h: 1080 },
      secondaryMedia: { x: 960, y: 0,   w: 960,  h: 1080 },
      headline:       { x: 80,  y: 820, w: 1760, h: 160  },
    };
    if (lid === 'L5') return {
      headline:    { x: 160, y: 350, w: 1600, h: 220 },
      subheadline: { x: 160, y: 580, w: 1600, h: 130 },
    };
    if (lid === 'L6') return {
      primaryMedia: { x: 0,  y: 0,    w: 1920, h: 920 },
      headline:     { x: 80, y: 930,  w: 1760, h: 110 },
      subheadline:  { x: 80, y: 1042, w: 1760, h: 50  },
    };
    if (lid === 'L7') return {
      bigNumber:   { x: 560, y: 380, w: 800,  h: 220 },
      subheadline: { x: 260, y: 630, w: 1400, h: 100 },
    };
    if (lid === 'L8') return {
      primaryMedia: { x: 80,  y: 80,  w: 672,  h: 900 },
      quoteText:    { x: 800, y: 250, w: 1040, h: 400 },
      subheadline:  { x: 800, y: 670, w: 1040, h: 100 },
    };
    if (lid === 'L10') return {
      primaryMedia:   { x: 60,  y: 60, w: 900, h: 500 },
      secondaryMedia: { x: 970, y: 60, w: 890, h: 500 },
      headline:       { x: 80,  y: 790, w: 1760, h: 160 },
    };
    if (lid === 'L12') return {
      headline:    { x: 120, y: 300, w: 1680, h: 400 },
      subheadline: { x: 120, y: 720, w: 1680, h: 100 },
    };
    // Default: L1 / L11 (full-screen visual)
    return {
      primaryMedia: { x: 0,  y: 0,   w: 1920, h: 1080 },
      headline:     { x: 80, y: 780, w: 1760, h: 180  },
      subheadline:  { x: 80, y: 960, w: 1760, h: 80   },
    };
  })();

  // Content layer drag handler
  const handleContentLayerDragStart = (e: React.MouseEvent, key: ContentLayerKey) => {
    e.preventDefault(); e.stopPropagation();
    captureSnapshot(); // snapshot before drag begins
    setSelectedLayer(key);
    if (!previewContainerRef.current) return;
    const rect = previewContainerRef.current.getBoundingClientRect();
    const scaleX = 1920 / rect.width;
    const scaleY = 1080 / rect.height;
    const startMouseX = e.clientX;
    const startMouseY = e.clientY;
    const startTransform = getLayerTransform(key);
    const onMove = (ev: MouseEvent) => {
      const ddx = Math.round((ev.clientX - startMouseX) * scaleX);
      const ddy = Math.round((ev.clientY - startMouseY) * scaleY);
      updateLayerTransform(key, { dx: startTransform.dx + ddx, dy: startTransform.dy + ddy });
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Mouse drag handler — converts screen delta to logical 1920×1080 delta
  const handleOverlayDragStart = (
    e: React.MouseEvent,
    key: keyof OverlayGeometry,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    captureSnapshot(); // snapshot before drag begins
    setSelectedOverlay(key);
    if (!previewContainerRef.current) return;

    const rect = previewContainerRef.current.getBoundingClientRect();
    const scaleX = 1920 / rect.width;
    const scaleY = 1080 / rect.height;
    const startMouseX = e.clientX;
    const startMouseY = e.clientY;
    const startGeo = getGeo(key);

    const onMove = (ev: MouseEvent) => {
      const dx = Math.round((ev.clientX - startMouseX) * scaleX);
      const dy = Math.round((ev.clientY - startMouseY) * scaleY);
      setEditConfig((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          overlayGeometry: {
            ...prev.overlayGeometry,
            [key]: { ...startGeo, x: startGeo.x + dx, y: startGeo.y + dy },
          },
        };
      });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Build preview props from editConfig + uploaded preview assets + editable text
  const previewProps: SceneProps = useMemo(() => {
    const layoutId = (LAYOUT_IDS.includes(editConfig?.baseLayoutId as SceneLayoutId)
      ? editConfig?.baseLayoutId
      : editConfig?.id) as SceneLayoutId ?? 'L1';
    return {
      layoutId,
      headline: prevText.headline,
      subheadline: (editConfig?.showSubheadline ?? true) ? prevText.subheadline : undefined,
      bigNumber: (editConfig?.showBigNumber ?? false) ? prevText.bigNumber : undefined,
      quoteText: (editConfig?.showQuote ?? false) ? prevText.quoteText : undefined,
      highlightWords: (editConfig?.showHighlightWords ?? false)
        ? prevText.headline.split(/\s+/).filter((w) => w.length >= 3).slice(0, 3)
        : undefined,
      colorPalette,
      headlineFont: { ...headlineFont, ...(textLayerStyle.headline ? { fontSize: textLayerStyle.headline.fontSize ?? headlineFont.fontSize } : {}) },
      subheadlineFont: { ...subheadlineFont, ...(textLayerStyle.subheadline ? { fontSize: textLayerStyle.subheadline.fontSize ?? subheadlineFont.fontSize } : {}) },
      assets: {
        primary: prevAssets.primaryImage ?? prevAssets.video,
        secondary: prevAssets.secondaryImage,
        video: prevAssets.video,
        logo: prevAssets.logo,
      },
      voiceoverUrl: prevAssets.voiceover?.url,
      bgMusicUrl: prevAssets.bgMusic?.url,
      voiceoverVolume: 0.9,
      bgMusicVolume: 0.3,
      showTicker: editConfig?.showTicker ?? true,
      tickerText: prevText.tickerText,
      tickerSpeed: 3,
      showLogo: editConfig?.showLogo ?? true,
      showTopBar: editConfig?.showTopBar ?? true,
      topBarText: prevText.topBarText,
      showStoryCounter: editConfig?.showStoryCounter ?? false,
      storyCounterText: 'खबर नंबर 1',
      hideImage: !(editConfig?.showImage ?? true),
      hideVideo: !(editConfig?.showVideo ?? true),
      videoMuted: editConfig?.videoMuted ?? false,
      overlayGeometry: editConfig?.overlayGeometry,
      overlayColors: editConfig?.overlayColors,
      contentLayerTransforms,
      customLayers,
    };
  }, [editConfig, colorPalette, headlineFont, subheadlineFont, prevAssets, prevText, textLayerStyle, contentLayerTransforms, customLayers]);

  // ── Auto-save scene config to localStorage ───────────────────
  useEffect(() => {
    if (!editConfig) return;
    const key = `buzz24_scene_v1_${editConfig.id}`;
    try {
      localStorage.setItem(key, JSON.stringify({ editConfig, contentLayerTransforms, customLayers }));
    } catch { /* storage full */ }
  }, [editConfig, contentLayerTransforms, customLayers]);

  // ── Export scene config as JSON ───────────────────────────────
  const handleExport = () => {
    if (!editConfig) return;
    const data = {
      version: '1',
      exportedAt: new Date().toISOString(),
      layoutConfig: editConfig,
      contentLayerTransforms,
      customLayers,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `buzz24-${editConfig.id}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Import scene config from JSON ────────────────────────────
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string);
        if (data.layoutConfig) { captureSnapshot(); setEditConfig(data.layoutConfig); }
        if (data.contentLayerTransforms) setContentLayerTransforms(data.contentLayerTransforms);
        if (data.customLayers) setCustomLayers(data.customLayers);
      } catch { alert('Invalid Buzz24 JSON file'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleSave = () => {
    if (!editConfig) return;
    const updated = upsertLayoutConfig(editConfig, layoutConfigs);
    onLayoutConfigsChange(updated);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  };

  const handleReset = () => {
    if (!editConfig) return;
    const def = DEFAULT_LAYOUT_CONFIGS.find((c) => c.id === editConfig.baseLayoutId || c.id === editConfig.id);
    if (def) setEditConfig({ ...def, id: editConfig.id, label: editConfig.label });
  };

  const handleAddCustom = () => {
    const base = layoutConfigs.find((c) => c.id === selectedId) ?? DEFAULT_LAYOUT_CONFIGS[0];
    const newId = `custom-${Date.now()}`;
    const newConfig: LayoutConfig = { ...base, id: newId, label: `Custom (${base.label})`, isCustom: true };
    const updated = upsertLayoutConfig(newConfig, layoutConfigs);
    onLayoutConfigsChange(updated);
    setSelectedId(newId);
  };

  const handleDelete = () => {
    if (!editConfig?.isCustom) return;
    const updated = deleteLayoutConfig(editConfig.id, layoutConfigs);
    onLayoutConfigsChange(updated);
    setSelectedId('L1');
  };

  const toggle = (key: keyof LayoutConfig) => {
    if (!editConfig) return;
    setEditConfig({ ...editConfig, [key]: !editConfig[key] });
  };

  const cfgBool = (key: keyof LayoutConfig): boolean => !!(editConfig?.[key]);

  const allConfigs = [
    ...LAYOUT_IDS.map((id) => layoutConfigs.find((c) => c.id === id) ?? DEFAULT_LAYOUT_CONFIGS.find((c) => c.id === id)!),
    ...layoutConfigs.filter((c) => c.isCustom),
  ].filter(Boolean) as LayoutConfig[];

  const panelLabel: React.CSSProperties = {
    fontSize: 9, fontWeight: 800, color: '#334155', letterSpacing: 1,
    textTransform: 'uppercase', marginBottom: 8, marginTop: 4,
    paddingBottom: 5, borderBottom: '1px solid #1e293b',
  };

  const cfgRow = (label: string, key: keyof LayoutConfig, icon: string, desc?: string) => (
    <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', borderRadius: 7, background: cfgBool(key) ? 'rgba(124,58,237,0.08)' : '#0a0f1e', border: `1px solid ${cfgBool(key) ? '#7c3aed40' : '#1e293b'}`, marginBottom: 4 }}>
      <div>
        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{icon} {label}</div>
        {desc && <div style={{ fontSize: 9, color: '#334155' }}>{desc}</div>}
      </div>
      <Toggle value={cfgBool(key)} onChange={() => toggle(key)} accentColor="#7c3aed" />
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.95)',
      display: 'flex', flexDirection: 'column', fontFamily: "'Inter','Roboto',sans-serif",
    }}>
      {/* Header */}
      <div style={{ height: 52, background: '#0d1117', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', padding: '0 20px', gap: 12, flexShrink: 0 }}>
        <div style={{ fontSize: 18 }}>📐</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#e2e8f0' }}>Layout Library Editor</div>
        <div style={{ fontSize: 11, color: '#475569' }}>Customize what each layout shows by default</div>
        <div style={{ flex: 1 }} />
        <button onClick={handleAddCustom} style={{ ...secondaryBtn, fontSize: 11, padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 5 }}>＋ Add Custom</button>
        <button onClick={onClose} style={{ ...secondaryBtn, padding: '5px 14px', fontSize: 12 }}>✕ Close</button>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left: Layout list */}
        <div style={{ width: 230, background: '#060b14', borderRight: '1px solid #1e293b', overflow: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {allConfigs.map((cfg) => (
            <div
              key={cfg.id}
              onClick={() => setSelectedId(cfg.id)}
              style={{
                background: selectedId === cfg.id ? '#1e293b' : '#0f172a',
                border: `1.5px solid ${selectedId === cfg.id ? '#7c3aed' : '#1e293b'}`,
                borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
                transition: 'all 0.12s', display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              <div style={{ flexShrink: 0 }}>{layoutWireframe(cfg.baseLayoutId || cfg.id)}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: selectedId === cfg.id ? '#c4b5fd' : '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                  {cfg.id.startsWith('custom') ? '⭐' : ''}{cfg.id}
                  {cfg.isCustom && <span style={{ fontSize: 8, color: '#7c3aed', background: '#7c3aed20', padding: '1px 4px', borderRadius: 3 }}>custom</span>}
                </div>
                <div style={{ fontSize: 9, color: '#475569', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cfg.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Center: Live preview + Edit Mode canvas */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 24px', background: '#030712', flexDirection: 'column', gap: 12 }}>
          {/* Edit / Preview mode toggle + Undo / Redo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 0, background: '#0f172a', borderRadius: 8, border: '1px solid #1e293b', overflow: 'hidden' }}>
              {(['preview', 'edit'] as const).map((mode) => (
                <button key={mode} onClick={() => { setEditMode(mode === 'edit'); setSelectedOverlay(null); }}
                  style={{ padding: '7px 18px', fontSize: 11, fontWeight: 700, cursor: 'pointer', border: 'none',
                    background: (editMode ? mode === 'edit' : mode === 'preview') ? '#7c3aed' : 'transparent',
                    color: (editMode ? mode === 'edit' : mode === 'preview') ? '#fff' : '#64748b',
                    transition: 'all 0.15s',
                  }}>
                  {mode === 'preview' ? '▶ Preview' : '✏️ Edit Mode'}
                </button>
              ))}
            </div>

            {/* Undo / Redo buttons */}
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={handleUndo}
                disabled={undoStack.length === 0}
                title="Undo (Cmd+Z)"
                style={{
                  width: 34, height: 34, borderRadius: 7, border: '1px solid #1e293b',
                  background: undoStack.length > 0 ? '#0f172a' : '#070d17',
                  color: undoStack.length > 0 ? '#94a3b8' : '#1e293b',
                  cursor: undoStack.length > 0 ? 'pointer' : 'not-allowed',
                  fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.1s',
                }}
              >↩</button>
              <button
                onClick={handleRedo}
                disabled={redoStack.length === 0}
                title="Redo (Cmd+Shift+Z)"
                style={{
                  width: 34, height: 34, borderRadius: 7, border: '1px solid #1e293b',
                  background: redoStack.length > 0 ? '#0f172a' : '#070d17',
                  color: redoStack.length > 0 ? '#94a3b8' : '#1e293b',
                  cursor: redoStack.length > 0 ? 'pointer' : 'not-allowed',
                  fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.1s',
                }}
              >↪</button>
              {undoStack.length > 0 && (
                <span style={{ fontSize: 9, color: '#334155', alignSelf: 'center', marginLeft: 2 }}>{undoStack.length} steps</span>
              )}
            </div>
          </div>
          {editMode && (
            <div style={{ fontSize: 10, color: '#7c3aed', background: 'rgba(124,58,237,0.1)', padding: '4px 12px', borderRadius: 6, border: '1px solid #7c3aed40' }}>
              Drag overlays to reposition · Select to resize &amp; adjust
            </div>
          )}

          <div style={{ width: '100%', maxWidth: 760 }}>
            {/* Preview container — drag canvas overlaid on top */}
            <div ref={previewContainerRef} style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', background: '#000', borderRadius: 10, overflow: 'hidden', boxShadow: '0 0 60px rgba(0,0,0,0.9)' }}>
              <div style={{ position: 'absolute', inset: 0 }}>
                <Player
                  key={`${selectedId}-${previewProps.layoutId}-${previewProps.hideImage}-${previewProps.hideVideo}-${previewProps.showTicker}-${JSON.stringify(previewProps.overlayGeometry)}-${JSON.stringify(contentLayerTransforms)}`}
                  component={SceneComposition as unknown as React.ComponentType<Record<string, unknown>>}
                  inputProps={previewProps as unknown as Record<string, unknown>}
                  durationInFrames={durationInFrames}
                  compositionWidth={1920}
                  compositionHeight={1080}
                  fps={fps}
                  style={{ width: '100%', height: '100%' }}
                  controls={!editMode}
                  autoPlay
                  loop
                />
              </div>

              {/* ── Edit Mode Drag Canvas ── */}
              {editMode && editConfig && (() => {
                // Overlay elements definition
                const OVERLAYS: { key: keyof OverlayGeometry; label: string; color: string; visible: boolean }[] = [
                  { key: 'ticker',         label: 'Ticker',         color: '#f59e0b', visible: editConfig.showTicker },
                  { key: 'topBar',         label: 'Top Bar',        color: '#3b82f6', visible: editConfig.showTopBar },
                  { key: 'logo',           label: 'Logo',           color: '#10b981', visible: editConfig.showLogo },
                  { key: 'storyCounter',   label: 'Story Counter',  color: '#8b5cf6', visible: editConfig.showStoryCounter },
                  { key: 'breakingBanner', label: 'Breaking Banner',color: '#ef4444', visible: editConfig.showBreakingBanner },
                ];

                // Content layer handles configuration
                const CONTENT_HANDLES: { key: ContentLayerKey; label: string; color: string; active: boolean }[] = [
                  { key: 'primaryMedia',   label: '📷 Primary',    color: '#06b6d4', active: editConfig.showImage || editConfig.showVideo },
                  { key: 'secondaryMedia', label: '📷 Secondary',  color: '#0891b2', active: editConfig.showImage },
                  { key: 'headline',       label: '📰 Headline',   color: '#f59e0b', active: editConfig.showHeadline },
                  { key: 'subheadline',    label: '📄 Sub',        color: '#d97706', active: editConfig.showSubheadline },
                  { key: 'bigNumber',      label: '🔢 BigNum',     color: '#a78bfa', active: editConfig.showBigNumber },
                  { key: 'quoteText',      label: '💬 Quote',      color: '#ec4899', active: editConfig.showQuote },
                ];

                return (
                  <div style={{ position: 'absolute', inset: 0, zIndex: 10 }}
                    onClick={(e) => { if (e.target === e.currentTarget) { setSelectedOverlay(null); setSelectedLayer(null); } }}>
                    {/* Overlay element handles */}
                    {OVERLAYS.map(({ key, label, color, visible: vis }) => {
                      if (!vis) return null;
                      const geo = getGeo(key);
                      const isSelected = selectedOverlay === key;
                      // Convert logical coords (1920×1080) to % for positioning
                      const lPct = (geo.x / 1920) * 100;
                      const tPct = (geo.y / 1080) * 100;
                      const wPct = (geo.w / 1920) * 100;
                      const hPct = (geo.h / 1080) * 100;

                      return (
                        <div
                          key={key}
                          onMouseDown={(e) => handleOverlayDragStart(e, key)}
                          onClick={(e) => { e.stopPropagation(); setSelectedOverlay(key); }}
                          style={{
                            position: 'absolute',
                            left: `${lPct}%`, top: `${tPct}%`,
                            width: `${wPct}%`, height: `${hPct}%`,
                            border: `2px solid ${color}`,
                            background: isSelected ? `${color}30` : `${color}15`,
                            cursor: 'move',
                            boxSizing: 'border-box',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'background 0.1s',
                          }}
                        >
                          <div style={{
                            background: color, color: '#000', fontSize: 9, fontWeight: 800,
                            padding: '2px 6px', borderRadius: 3, pointerEvents: 'none',
                            letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap',
                          }}>
                            {label}
                          </div>
                          {/* Resize handle — bottom-right corner */}
                          <div
                            onMouseDown={(e) => {
                              e.preventDefault(); e.stopPropagation();
                              captureSnapshot();
                              const rect = previewContainerRef.current?.getBoundingClientRect();
                              if (!rect) return;
                              const scaleX = 1920 / rect.width;
                              const scaleY = 1080 / rect.height;
                              const startX = e.clientX;
                              const startY = e.clientY;
                              const startGeo = getGeo(key);
                              const onMove = (ev: MouseEvent) => {
                                const dw = Math.round((ev.clientX - startX) * scaleX);
                                const dh = Math.round((ev.clientY - startY) * scaleY);
                                setEditConfig((prev) => prev ? {
                                  ...prev,
                                  overlayGeometry: {
                                    ...prev.overlayGeometry,
                                    [key]: { ...startGeo, w: Math.max(40, startGeo.w + dw), h: Math.max(20, startGeo.h + dh) },
                                  },
                                } : prev);
                              };
                              const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                              document.addEventListener('mousemove', onMove);
                              document.addEventListener('mouseup', onUp);
                            }}
                            style={{
                              position: 'absolute', bottom: 0, right: 0,
                              width: 12, height: 12, background: color, cursor: 'se-resize',
                              borderRadius: '2px 0 0 0',
                            }}
                          />
                        </div>
                      );
                    })}

                    {/* Content layer full bounding boxes (same style as overlay element frames) */}
                    {CONTENT_HANDLES.filter((h) => h.active && CONTENT_LAYER_BOUNDS[h.key]).map(({ key, label, color }) => {
                      const bounds = CONTENT_LAYER_BOUNDS[key]!;
                      const lt = getLayerTransform(key);
                      if (!lt.visible) return null;

                      // Use explicit w/h when set; otherwise derive from scale (centred)
                      const effectiveW = lt.w && lt.w > 0 ? lt.w : bounds.w * lt.scale;
                      const effectiveH = lt.h && lt.h > 0 ? lt.h : bounds.h * lt.scale;
                      const originX = lt.w && lt.w > 0 ? bounds.x : (bounds.x + bounds.w / 2 - effectiveW / 2);
                      const originY = lt.h && lt.h > 0 ? bounds.y : (bounds.y + bounds.h / 2 - effectiveH / 2);
                      const fx = (originX + lt.dx) / 1920 * 100;
                      const fy = (originY + lt.dy) / 1080 * 100;
                      const fw = effectiveW / 1920 * 100;
                      const fh = effectiveH / 1080 * 100;

                      const isSelCL = selectedLayer === key;

                      // Thumbnail asset for media layers
                      const mediaAsset = key === 'primaryMedia'
                        ? (prevAssets.primaryImage ?? prevAssets.video)
                        : key === 'secondaryMedia' ? prevAssets.secondaryImage : undefined;

                      return (
                        <div
                          key={key}
                          onMouseDown={(e) => handleContentLayerDragStart(e, key)}
                          onClick={(e) => { e.stopPropagation(); setSelectedLayer(isSelCL ? null : key); setSelectedOverlay(null); }}
                          style={{
                            position: 'absolute',
                            left: `${fx}%`, top: `${fy}%`,
                            width: `${fw}%`, height: `${fh}%`,
                            border: `2px solid ${color}`,
                            background: isSelCL ? `${color}30` : `${color}10`,
                            cursor: 'move', boxSizing: 'border-box',
                            overflow: 'hidden', transition: 'background 0.1s',
                            boxShadow: isSelCL ? `0 0 0 1px ${color}` : 'none',
                          }}
                        >
                          {/* Actual image/video thumbnail inside media boxes */}
                          {mediaAsset?.type === 'image' && (
                            <img src={mediaAsset.url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: isSelCL ? 0.45 : 0.25, pointerEvents: 'none' }} />
                          )}
                          {mediaAsset?.type === 'video' && (
                            <video src={mediaAsset.url} muted style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: isSelCL ? 0.45 : 0.25, pointerEvents: 'none' }} />
                          )}

                          {/* Label badge */}
                          <div style={{
                            position: 'absolute', top: 4, left: 4, zIndex: 1,
                            background: color, color: '#000', fontSize: 9, fontWeight: 800,
                            padding: '2px 6px', borderRadius: 3, pointerEvents: 'none',
                            letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap',
                          }}>
                            {label}
                            {(lt.dx !== 0 || lt.dy !== 0 || (lt.w && lt.w > 0) || lt.scale !== 1) && (
                              <span style={{ opacity: 0.75 }}>
                                {lt.w && lt.w > 0 ? ` · ${Math.round(lt.w)}×${Math.round(lt.h ?? 0)}` : lt.scale !== 1 ? ` · ${Math.round(lt.scale * 100)}%` : ''}
                                {lt.dx !== 0 || lt.dy !== 0 ? ` (${lt.dx > 0 ? '+' : ''}${Math.round(lt.dx)},${lt.dy > 0 ? '+' : ''}${Math.round(lt.dy)})` : ''}
                              </span>
                            )}
                          </div>

                          {/* Resize grip — bottom-right corner, resizes w/h */}
                          <div
                            onMouseDown={(e) => {
                              e.preventDefault(); e.stopPropagation();
                              captureSnapshot();
                              const rect = previewContainerRef.current?.getBoundingClientRect();
                              if (!rect) return;
                              const scaleX = 1920 / rect.width;
                              const scaleY = 1080 / rect.height;
                              const startX = e.clientX;
                              const startY = e.clientY;
                              const startLt = getLayerTransform(key);
                              const startW = startLt.w && startLt.w > 0 ? startLt.w : bounds.w * startLt.scale;
                              const startH = startLt.h && startLt.h > 0 ? startLt.h : bounds.h * startLt.scale;
                              const onMove = (ev: MouseEvent) => {
                                const newW = Math.max(40, Math.round(startW + (ev.clientX - startX) * scaleX));
                                const newH = Math.max(20, Math.round(startH + (ev.clientY - startY) * scaleY));
                                updateLayerTransform(key, { w: newW, h: newH });
                              };
                              const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                              document.addEventListener('mousemove', onMove);
                              document.addEventListener('mouseup', onUp);
                            }}
                            style={{
                              position: 'absolute', bottom: 0, right: 0,
                              width: 16, height: 16, background: color,
                              cursor: 'se-resize', borderRadius: '3px 0 0 0',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 10, color: '#000', fontWeight: 900, userSelect: 'none',
                            }}
                          >⤡</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 800, background: '#7c3aed', color: '#fff', padding: '2px 9px', borderRadius: 5 }}>{editConfig?.id}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>{editConfig?.label}</span>
              {editMode
                ? <span style={{ fontSize: 10, color: '#f59e0b' }}>✏️ Edit mode — drag elements, adjust properties on right →</span>
                : <span style={{ fontSize: 10, color: '#475569' }}>▶ Preview mode — edit settings on right →</span>}
            </div>
          </div>
        </div>

        {/* Right: Layer-based editor */}
        <div style={{ width: 320, background: '#060b14', borderLeft: '1px solid #1e293b', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {editConfig ? (
            <>
              {/* ── Fixed top: name + 3 tabs ── */}
              <div style={{ padding: '12px 14px 0', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
                <input
                  value={editConfig.label}
                  onChange={(e) => setEditConfig({ ...editConfig, label: e.target.value })}
                  style={{ ...inputStyle, fontSize: 12, marginBottom: 10 }}
                  placeholder="Layout name…"
                />
                <div style={{ display: 'flex', gap: 0 }}>
                  {(['layers', 'settings', 'content'] as const).map((tab) => {
                    const labels = { layers: '📋 Layers', settings: '⚙️ Settings', content: '📝 Content' };
                    return (
                      <button key={tab}
                        onClick={() => setRightTab(tab)}
                        style={{
                          flex: 1, padding: '8px 4px', border: 'none', cursor: 'pointer',
                          background: rightTab === tab ? '#0f172a' : 'transparent',
                          color: rightTab === tab ? '#c4b5fd' : '#475569',
                          fontSize: 10, fontWeight: rightTab === tab ? 700 : 500,
                          borderBottom: `2px solid ${rightTab === tab ? '#7c3aed' : 'transparent'}`,
                          transition: 'all 0.15s',
                        }}
                      >
                        {labels[tab]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Export / Import bar ── */}
              <div style={{ display: 'flex', gap: 4, padding: '6px 10px', borderBottom: '1px solid #0f172a', flexShrink: 0 }}>
                <button onClick={handleExport} title="Export scene settings as JSON"
                  style={{ flex: 1, padding: '4px 0', fontSize: 10, border: '1px solid #1e293b', borderRadius: 5, background: '#0f172a', color: '#64748b', cursor: 'pointer' }}>
                  ⬆ Export
                </button>
                <label title="Import scene settings from JSON"
                  style={{ flex: 1, padding: '4px 0', fontSize: 10, border: '1px solid #1e293b', borderRadius: 5, background: '#0f172a', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  ⬇ Import
                  <input type="file" accept=".json" style={{ display: 'none' }} onChange={handleImport} />
                </label>
              </div>

              {/* ── Scrollable tab body ── */}
              <div style={{ flex: 1, overflow: 'auto', padding: '10px 14px' }}>
                {(() => {
                  // ── Shared layer definitions ──
                  const LAYER_TO_CONTENT_KEY: Record<string, ContentLayerKey> = {
                    'primaryImage': 'primaryMedia', 'video': 'primaryMedia',
                    'secondaryImage': 'secondaryMedia',
                    'headline': 'headline', 'subheadline': 'subheadline',
                    'bigNumber': 'bigNumber', 'quoteText': 'quoteText',
                  };
                  type LKey = string;
                  interface LDef {
                    key: LKey; label: string; icon: string; group: string;
                    enabled: boolean; onToggle?: () => void;
                    hasAsset?: boolean; assetKey?: keyof typeof prevAssets; assetAccept?: string;
                  }
                  const layers: LDef[] = [
                    { key: 'primaryImage', label: 'Primary Image', icon: '🖼', group: 'Media',
                      enabled: editConfig.showImage, onToggle: () => setEditConfigWithHistory({ ...editConfig, showImage: !editConfig.showImage }),
                      hasAsset: true, assetKey: 'primaryImage', assetAccept: 'image/*' },
                    { key: 'secondaryImage', label: 'Secondary Image', icon: '🖼', group: 'Media',
                      enabled: editConfig.showImage, hasAsset: true, assetKey: 'secondaryImage', assetAccept: 'image/*' },
                    { key: 'video', label: 'Video', icon: '🎬', group: 'Media',
                      enabled: editConfig.showVideo, onToggle: () => setEditConfigWithHistory({ ...editConfig, showVideo: !editConfig.showVideo }),
                      hasAsset: true, assetKey: 'video', assetAccept: 'video/*' },
                    { key: 'logo', label: 'Logo', icon: '🏷️', group: 'Media',
                      enabled: editConfig.showLogo, onToggle: () => setEditConfigWithHistory({ ...editConfig, showLogo: !editConfig.showLogo }),
                      hasAsset: true, assetKey: 'logo', assetAccept: 'image/*' },
                    { key: 'voiceover', label: 'Voiceover', icon: '🎙', group: 'Audio',
                      enabled: editConfig.voiceoverEnabled, onToggle: () => setEditConfigWithHistory({ ...editConfig, voiceoverEnabled: !editConfig.voiceoverEnabled }),
                      hasAsset: true, assetKey: 'voiceover', assetAccept: 'audio/*' },
                    { key: 'bgMusic', label: 'BG Music', icon: '🎵', group: 'Audio',
                      enabled: editConfig.bgMusicEnabled, onToggle: () => setEditConfigWithHistory({ ...editConfig, bgMusicEnabled: !editConfig.bgMusicEnabled }),
                      hasAsset: true, assetKey: 'bgMusic', assetAccept: 'audio/*' },
                    { key: 'headline', label: 'Headline', icon: '📰', group: 'Text',
                      enabled: editConfig.showHeadline, onToggle: () => setEditConfigWithHistory({ ...editConfig, showHeadline: !editConfig.showHeadline }) },
                    { key: 'subheadline', label: 'Subheadline', icon: '📄', group: 'Text',
                      enabled: editConfig.showSubheadline, onToggle: () => setEditConfigWithHistory({ ...editConfig, showSubheadline: !editConfig.showSubheadline }) },
                    { key: 'bigNumber', label: 'Big Number', icon: '🔢', group: 'Text',
                      enabled: editConfig.showBigNumber, onToggle: () => setEditConfigWithHistory({ ...editConfig, showBigNumber: !editConfig.showBigNumber }) },
                    { key: 'quoteText', label: 'Quote Text', icon: '💬', group: 'Text',
                      enabled: editConfig.showQuote, onToggle: () => setEditConfigWithHistory({ ...editConfig, showQuote: !editConfig.showQuote }) },
                    { key: 'highlightWords', label: 'Highlight Words', icon: '✨', group: 'Text',
                      enabled: editConfig.showHighlightWords, onToggle: () => setEditConfigWithHistory({ ...editConfig, showHighlightWords: !editConfig.showHighlightWords }) },
                    { key: 'ticker', label: 'Ticker', icon: '📰', group: 'Overlays',
                      enabled: editConfig.showTicker, onToggle: () => setEditConfigWithHistory({ ...editConfig, showTicker: !editConfig.showTicker }) },
                    { key: 'topBar', label: 'Top Bar', icon: '📊', group: 'Overlays',
                      enabled: editConfig.showTopBar, onToggle: () => setEditConfigWithHistory({ ...editConfig, showTopBar: !editConfig.showTopBar }) },
                    { key: 'storyCounter', label: 'Story Counter', icon: '🔢', group: 'Overlays',
                      enabled: editConfig.showStoryCounter, onToggle: () => setEditConfigWithHistory({ ...editConfig, showStoryCounter: !editConfig.showStoryCounter }) },
                    { key: 'breakingBanner', label: 'Breaking Banner', icon: '🔴', group: 'Overlays',
                      enabled: editConfig.showBreakingBanner, onToggle: () => setEditConfigWithHistory({ ...editConfig, showBreakingBanner: !editConfig.showBreakingBanner }) },
                  ];

                  const curLayer = layers.find((l) => l.key === selectedLayer) ?? null;
                  const groups = ['Media', 'Audio', 'Text', 'Overlays'];
                  const grpIcons: Record<string, string> = { Media: '📷', Audio: '🎵', Text: '✍️', Overlays: '📊' };

                  // ── stepperRow: called as a function (NOT as <JSX/>), avoids React unmount on re-render ──
                  const clampVal = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, parseFloat(v.toFixed(6))));
                  const stepperRow = (label: string, value: number, min: number, max: number, step: number, unit: string | undefined, onChange: (v: number) => void, onCapture?: () => void) => {
                    return (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: 10, color: '#64748b', width: 62, flexShrink: 0 }}>{label}</span>
                        <div style={{ display: 'flex', alignItems: 'stretch', flex: 1, background: '#0a0f1e', border: '1px solid #1e293b', borderRadius: 6, overflow: 'hidden' }}>
                          <button
                            onClick={(e) => { e.preventDefault(); onCapture?.(); onChange(clampVal(value - step, min, max)); }}
                            style={{ padding: '4px 9px', border: 'none', borderRight: '1px solid #1e293b', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: 10, lineHeight: 1 }}>▼</button>
                          <StepperInput value={value} min={min} max={max} step={step} onChange={onChange} onCapture={onCapture} />
                          <button
                            onClick={(e) => { e.preventDefault(); onCapture?.(); onChange(clampVal(value + step, min, max)); }}
                            style={{ padding: '4px 9px', border: 'none', borderLeft: '1px solid #1e293b', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: 10, lineHeight: 1 }}>▲</button>
                        </div>
                        {unit && <span style={{ fontSize: 10, color: '#475569', width: 18, flexShrink: 0 }}>{unit}</span>}
                      </div>
                    );
                  };

                  // ════════════════════════════════
                  // LAYERS TAB
                  // ════════════════════════════════
                  if (rightTab === 'layers') {
                    return (
                      <>
                        {groups.map((grp) => {
                          const grpLayers = layers.filter((l) => l.group === grp);
                          return (
                            <div key={grp} style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: 9, fontWeight: 800, color: '#334155', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid #1e293b' }}>
                                {grpIcons[grp]} {grp}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {grpLayers.map((layer) => {
                                  const isSelected = selectedLayer === layer.key;
                                  const asset = layer.assetKey ? prevAssets[layer.assetKey] : undefined;
                                  return (
                                    <div key={layer.key}
                                      onClick={() => setSelectedLayer(isSelected ? null : layer.key)}
                                      style={{
                                        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                                        background: isSelected ? 'rgba(124,58,237,0.15)' : (layer.enabled ? '#0f172a' : '#070d17'),
                                        border: `1px solid ${isSelected ? '#7c3aed' : (layer.enabled ? '#1e293b' : '#0f1a28')}`,
                                        borderRadius: 7, cursor: 'pointer', opacity: layer.enabled ? 1 : 0.5,
                                        transition: 'all 0.1s',
                                      }}
                                    >
                                      <span style={{ fontSize: 14, flexShrink: 0 }}>{layer.icon}</span>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: isSelected ? '#c4b5fd' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{layer.label}</div>
                                        {asset && <div style={{ fontSize: 9, color: '#22c55e' }}>✓ {asset.name.slice(0, 18)}</div>}
                                        {!asset && layer.hasAsset && <div style={{ fontSize: 9, color: '#334155' }}>No file</div>}
                                      </div>
                                      {layer.onToggle && (
                                        <div onClick={(e) => { e.stopPropagation(); layer.onToggle?.(); }}>
                                          <Toggle value={layer.enabled} onChange={() => layer.onToggle?.()} accentColor="#7c3aed" />
                                        </div>
                                      )}
                                      {isSelected && <span style={{ fontSize: 8, color: '#7c3aed', flexShrink: 0 }}>●</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                        {/* ── Custom Layers ── */}
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid #1e293b' }}>
                            <span style={{ fontSize: 9, fontWeight: 800, color: '#334155', letterSpacing: 1, textTransform: 'uppercase' }}>✨ Custom</span>
                            <div style={{ display: 'flex', gap: 3 }}>
                              {(['text', 'image', 'video', 'animation'] as const).map((type) => {
                                const icons: Record<CustomLayerType, string> = { text: 'T', image: '🖼', video: '🎬', animation: '✨' };
                                return (
                                  <button key={type} title={`Add ${type} layer`}
                                    onClick={() => {
                                      const id = `cl-${Date.now()}`;
                                      const newLayer: CustomLayer = {
                                        id, type, label: `${type.charAt(0).toUpperCase() + type.slice(1)} ${customLayers.filter((l) => l.type === type).length + 1}`,
                                        x: 100, y: 100, w: 400, h: 200,
                                        zIndex: 20 + customLayers.length,
                                        opacity: 1, visible: true,
                                        text: type === 'text' ? 'Text here' : undefined,
                                        bgColor: type === 'animation' ? 'rgba(124,58,237,0.85)' : undefined,
                                        animationType: 'fade-in', animationDelay: 0, animationDuration: 20,
                                      };
                                      setCustomLayers((prev) => [...prev, newLayer]);
                                      setSelectedLayer(`custom-${id}`);
                                      setRightTab('settings');
                                    }}
                                    style={{ padding: '2px 7px', fontSize: 10, border: '1px solid #1e293b', borderRadius: 4, background: '#0f172a', color: '#64748b', cursor: 'pointer' }}>
                                    + {icons[type]}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          {customLayers.length === 0 ? (
                            <div style={{ fontSize: 10, color: '#1e293b', textAlign: 'center', padding: '8px 0' }}>+ से layer add करें</div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {customLayers.map((cl) => {
                                const isSelCL = selectedLayer === `custom-${cl.id}`;
                                const typeIcons: Record<CustomLayerType, string> = { text: '🔤', image: '🖼', video: '🎬', animation: '✨' };
                                return (
                                  <div key={cl.id}
                                    onClick={() => { setSelectedLayer(isSelCL ? null : `custom-${cl.id}`); setRightTab('settings'); }}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: isSelCL ? 'rgba(124,58,237,0.15)' : '#0f172a', border: `1px solid ${isSelCL ? '#7c3aed' : '#1e293b'}`, borderRadius: 7, cursor: 'pointer', transition: 'all 0.1s' }}>
                                    <span style={{ fontSize: 14, flexShrink: 0 }}>{typeIcons[cl.type]}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 11, fontWeight: 600, color: isSelCL ? '#c4b5fd' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cl.label}</div>
                                      <div style={{ fontSize: 9, color: '#334155' }}>z:{cl.zIndex} · {cl.w}×{cl.h}</div>
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <Toggle value={cl.visible} onChange={() => setCustomLayers((prev) => prev.map((l) => l.id === cl.id ? { ...l, visible: !l.visible } : l))} accentColor="#7c3aed" />
                                    </div>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setCustomLayers((prev) => prev.filter((l) => l.id !== cl.id)); if (isSelCL) setSelectedLayer(null); }}
                                      style={{ padding: '2px 5px', fontSize: 9, border: '1px solid #ef444430', borderRadius: 3, background: 'transparent', color: '#ef4444', cursor: 'pointer' }}>✕</button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div style={{ fontSize: 10, color: '#1e293b', textAlign: 'center', paddingTop: 4 }}>
                          Select a layer, then use Settings / Content tabs
                        </div>
                      </>
                    );
                  }

                  // ════════════════════════════════
                  // SETTINGS TAB
                  // ════════════════════════════════
                  if (rightTab === 'settings') {
                    // ── Custom layer selected → show its settings ──
                    const customLayerId2 = selectedLayer?.startsWith('custom-')
                      ? selectedLayer.slice('custom-'.length) : null;
                    const selCL = customLayerId2 ? customLayers.find((l) => l.id === customLayerId2) ?? null : null;
                    if (selCL) {
                      const updateCL = (patch: Partial<CustomLayer>) =>
                        setCustomLayers((prev) => prev.map((l) => l.id === selCL.id ? { ...l, ...patch } : l));
                      const ANIM_TYPES: CustomLayer['animationType'][] = ['none', 'fade-in', 'slide-left', 'slide-right', 'slide-up', 'slide-down', 'zoom-in', 'pulse'];
                      const OBJECT_FITS: CustomLayer['objectFit'][] = ['cover', 'contain', 'fill'];

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {/* Header */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#0f172a', borderRadius: 7 }}>
                            <span style={{ fontSize: 14 }}>{ ({'text':'🔤','image':'🖼','video':'🎬','animation':'✨'} as Record<string,string>)[selCL.type] }</span>
                            <input value={selCL.label} onChange={(e) => updateCL({ label: e.target.value })}
                              style={{ flex: 1, background: 'transparent', border: 'none', color: '#c4b5fd', fontSize: 12, fontWeight: 600, outline: 'none' }} />
                          </div>

                          {/* Position & Size */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            <div style={{ fontSize: 9, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 6, borderBottom: '1px solid #1e293b' }}>📐 Position &amp; Size</div>
                            {stepperRow('Position X', selCL.x, 0, 1920, 1, 'px', (v) => updateCL({ x: v }))}
                            {stepperRow('Position Y', selCL.y, 0, 1080, 1, 'px', (v) => updateCL({ y: v }))}
                            {stepperRow('Width',  selCL.w, 10, 1920, 1, 'px', (v) => updateCL({ w: v }))}
                            {stepperRow('Height', selCL.h, 10, 1080, 1, 'px', (v) => updateCL({ h: v }))}
                            {stepperRow('Opacity', selCL.opacity, 0, 1, 0.05, undefined, (v) => updateCL({ opacity: v }))}
                            {stepperRow('Z-Index', selCL.zIndex, 0, 100, 1, undefined, (v) => updateCL({ zIndex: v }))}
                          </div>

                          {/* Entrance Animation */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            <div style={{ fontSize: 9, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 6, borderBottom: '1px solid #1e293b' }}>🎞 Animation</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {ANIM_TYPES.map((at) => (
                                <button key={at} onClick={() => updateCL({ animationType: at })}
                                  style={{ padding: '3px 7px', fontSize: 9, borderRadius: 4, border: `1px solid ${selCL.animationType === at ? '#7c3aed' : '#1e293b'}`, background: selCL.animationType === at ? 'rgba(124,58,237,0.2)' : '#0f172a', color: selCL.animationType === at ? '#c4b5fd' : '#475569', cursor: 'pointer' }}>
                                  {at}
                                </button>
                              ))}
                            </div>
                            {stepperRow('Delay', selCL.animationDelay ?? 0, 0, 120, 1, 'f', (v) => updateCL({ animationDelay: v }))}
                            {stepperRow('Duration', selCL.animationDuration ?? 20, 5, 120, 1, 'f', (v) => updateCL({ animationDuration: v }))}
                          </div>

                          {/* Type-specific settings */}
                          {selCL.type === 'text' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                              <div style={{ fontSize: 9, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 6, borderBottom: '1px solid #1e293b' }}>🔤 Text Style</div>
                              {stepperRow('Font Size', selCL.fontSize ?? 52, 10, 200, 1, 'px', (v) => updateCL({ fontSize: v }))}
                              {stepperRow('Font Wt',  selCL.fontWeight ?? 700, 100, 900, 100, undefined, (v) => updateCL({ fontWeight: v }))}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 10, color: '#64748b', width: 62, flexShrink: 0 }}>Text Color</span>
                                <input type="color" value={selCL.color ?? '#ffffff'} onChange={(e) => updateCL({ color: e.target.value })} style={{ width: 36, height: 28, border: '1px solid #1e293b', borderRadius: 4, background: 'none', cursor: 'pointer', padding: 2 }} />
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 10, color: '#64748b', width: 62, flexShrink: 0 }}>BG Color</span>
                                <input type="color" value={selCL.bgColor?.slice(0,7) ?? '#000000'} onChange={(e) => updateCL({ bgColor: e.target.value })} style={{ width: 36, height: 28, border: '1px solid #1e293b', borderRadius: 4, background: 'none', cursor: 'pointer', padding: 2 }} />
                                {selCL.bgColor && <button onClick={() => updateCL({ bgColor: undefined })} style={{ ...secondaryBtn, fontSize: 9, padding: '1px 6px' }}>↺</button>}
                              </div>
                              <div style={{ display: 'flex', gap: 4 }}>
                                {(['left','center','right'] as const).map((align) => (
                                  <button key={align} onClick={() => updateCL({ textAlign: align })}
                                    style={{ flex: 1, padding: '4px 0', fontSize: 10, borderRadius: 4, border: `1px solid ${selCL.textAlign === align ? '#7c3aed' : '#1e293b'}`, background: selCL.textAlign === align ? 'rgba(124,58,237,0.2)' : '#0f172a', color: selCL.textAlign === align ? '#c4b5fd' : '#475569', cursor: 'pointer' }}>
                                    {align === 'left' ? 'Left' : align === 'center' ? 'Center' : 'Right'}
                                  </button>
                                ))}
                              </div>
                              <textarea value={selCL.text ?? ''} onChange={(e) => updateCL({ text: e.target.value })}
                                placeholder="Text content…"
                                style={{ ...inputStyle, minHeight: 64, resize: 'vertical', fontSize: 11 }} />
                            </div>
                          )}

                          {(selCL.type === 'image' || selCL.type === 'video') && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                              <div style={{ fontSize: 9, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 6, borderBottom: '1px solid #1e293b' }}>📂 Media</div>
                              <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: 12, border: `2px dashed ${selCL.src ? '#22c55e50' : '#1e293b'}`, borderRadius: 8, cursor: 'pointer', background: selCL.src ? '#05250f40' : '#0a0f1e' }}>
                                {selCL.src
                                  ? selCL.type === 'image'
                                    ? <img src={selCL.src} alt="" style={{ width: '100%', height: 60, objectFit: 'cover', borderRadius: 4 }} />
                                    : <div style={{ fontSize: 10, color: '#22c55e' }}>✓ Video loaded</div>
                                  : <div style={{ fontSize: 11, color: '#334155' }}>Upload {selCL.type}</div>}
                                <div style={{ fontSize: 10, color: selCL.src ? '#22c55e' : '#7c3aed', fontWeight: 600 }}>{selCL.src ? '↺ Replace' : '＋ Upload'}</div>
                                <input type="file" accept={selCL.type === 'image' ? 'image/*' : 'video/*'} style={{ display: 'none' }}
                                  onChange={(e) => { const f = e.target.files?.[0]; if (f) updateCL({ src: URL.createObjectURL(f) }); }} />
                              </label>
                              {selCL.src && <button onClick={() => updateCL({ src: undefined })} style={{ ...secondaryBtn, fontSize: 10, padding: '4px 0', width: '100%', color: '#ef4444', borderColor: '#ef444430' }}>🗑 Remove</button>}
                              <div style={{ display: 'flex', gap: 4 }}>
                                {OBJECT_FITS.map((fit) => (
                                  <button key={fit} onClick={() => updateCL({ objectFit: fit })}
                                    style={{ flex: 1, padding: '4px 0', fontSize: 9, borderRadius: 4, border: `1px solid ${selCL.objectFit === fit ? '#7c3aed' : '#1e293b'}`, background: selCL.objectFit === fit ? 'rgba(124,58,237,0.2)' : '#0f172a', color: selCL.objectFit === fit ? '#c4b5fd' : '#475569', cursor: 'pointer' }}>
                                    {fit}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {selCL.type === 'animation' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                              <div style={{ fontSize: 9, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 6, borderBottom: '1px solid #1e293b' }}>🎨 Color</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 10, color: '#64748b', width: 62, flexShrink: 0 }}>Color</span>
                                <input type="color" value={selCL.bgColor?.slice(0,7) ?? '#7c3aed'} onChange={(e) => updateCL({ bgColor: e.target.value })} style={{ width: 36, height: 28, border: '1px solid #1e293b', borderRadius: 4, background: 'none', cursor: 'pointer', padding: 2 }} />
                              </div>
                            </div>
                          )}

                          {/* Delete */}
                          <button onClick={() => { setCustomLayers((prev) => prev.filter((l) => l.id !== selCL.id)); setSelectedLayer(null); }}
                            style={{ ...secondaryBtn, fontSize: 10, padding: '6px 0', width: '100%', color: '#ef4444', borderColor: '#ef444430', marginTop: 4 }}>
                            🗑 Delete Layer
                          </button>
                        </div>
                      );
                    }

                    // No layer selected → global settings
                    if (!curLayer) {
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                          <div>
                            <div style={{ fontSize: 9, fontWeight: 800, color: '#334155', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid #1e293b' }}>Screen Style</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 4 }}>
                              {SCREEN_STYLES.map((ss) => (
                                <button key={ss.id}
                                  onClick={() => setEditConfigWithHistory({ ...editConfig, screenStyle: ss.id, baseLayoutId: SCREEN_STYLE_BASE[ss.id] })}
                                  style={{
                                    background: editConfig.screenStyle === ss.id ? 'rgba(124,58,237,0.2)' : '#0f172a',
                                    border: `1px solid ${editConfig.screenStyle === ss.id ? '#7c3aed' : '#1e293b'}`,
                                    borderRadius: 6, padding: '6px 4px', cursor: 'pointer', color: '#fff', textAlign: 'center',
                                  }}>
                                  <div style={{ fontSize: 13 }}>{ss.icon}</div>
                                  <div style={{ fontSize: 8, color: editConfig.screenStyle === ss.id ? '#c4b5fd' : '#475569', marginTop: 2 }}>{ss.label}</div>
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 9, fontWeight: 800, color: '#334155', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid #1e293b' }}>⏱ Default Duration</div>
                            {stepperRow('Duration', editConfig.defaultDuration, 2, 30, 0.5, 's', (v) => setEditConfig({ ...editConfig, defaultDuration: v }))}
                          </div>
                          <div style={{ fontSize: 11, color: '#334155', textAlign: 'center', paddingTop: 4 }}>
                            ← Select a layer from Layers tab for layer-specific settings
                          </div>
                        </div>
                      );
                    }

                    // Layer selected → layer-specific settings
                    const clKey = LAYER_TO_CONTENT_KEY[curLayer.key] as ContentLayerKey | undefined;
                    // Logo is in Media group but lives in overlay geometry
                    const validOverlayKeys: (keyof OverlayGeometry)[] = ['ticker', 'topBar', 'logo', 'storyCounter', 'breakingBanner'];
                    const oKey = curLayer.key as keyof OverlayGeometry;
                    const isValidOverlay = validOverlayKeys.includes(oKey);

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {/* Layer identity chip */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#0f172a', borderRadius: 7 }}>
                          <span style={{ fontSize: 16 }}>{curLayer.icon}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#c4b5fd' }}>{curLayer.label}</span>
                        </div>

                        {/* On/Off toggle — for every layer that has one */}
                        {curLayer.onToggle && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 7 }}>
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>Enabled</span>
                            <Toggle value={curLayer.enabled} onChange={() => { curLayer.onToggle?.(); }} accentColor="#7c3aed" />
                          </div>
                        )}

                        {/* ── Content-layer transform (image / video / text layers) ── */}
                        {clKey && (() => {
                          const lt = getLayerTransform(clKey);
                          const layerBounds = CONTENT_LAYER_BOUNDS[clKey] ?? { x: 0, y: 0, w: 1920, h: 1080 };
                          const curW = lt.w && lt.w > 0 ? lt.w : layerBounds.w;
                          const curH = lt.h && lt.h > 0 ? lt.h : layerBounds.h;
                          const hasChanges = lt.dx !== 0 || lt.dy !== 0 || lt.opacity !== 1 || !lt.visible
                            || (lt.w && lt.w > 0 && lt.w !== layerBounds.w) || (lt.h && lt.h > 0 && lt.h !== layerBounds.h);
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 6, borderBottom: '1px solid #1e293b' }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#06b6d4', textTransform: 'uppercase', letterSpacing: 0.5 }}>📐 Position &amp; Size</div>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                  <span style={{ fontSize: 10, color: '#64748b' }}>Visible</span>
                                  <Toggle value={lt.visible} onChange={() => updateLayerTransform(clKey, { visible: !lt.visible })} accentColor="#06b6d4" />
                                  {hasChanges && (
                                    <button onClick={() => resetLayerTransform(clKey)} style={{ ...secondaryBtn, fontSize: 9, padding: '2px 6px', color: '#06b6d4', borderColor: '#06b6d430' }}>↺</button>
                                  )}
                                </div>
                              </div>
                              {stepperRow('Position X', lt.dx, -1920, 1920, 1, 'px', (v) => updateLayerTransform(clKey, { dx: v }), captureSnapshot)}
                              {stepperRow('Position Y', lt.dy, -1080, 1080, 1, 'px', (v) => updateLayerTransform(clKey, { dy: v }), captureSnapshot)}
                              {stepperRow('Width',  curW, 40, 3840, 1, 'px', (v) => updateLayerTransform(clKey, { w: v }), captureSnapshot)}
                              {stepperRow('Height', curH, 20, 2160, 1, 'px', (v) => updateLayerTransform(clKey, { h: v }), captureSnapshot)}
                              {stepperRow('Opacity', lt.opacity, 0, 1, 0.05, undefined, (v) => updateLayerTransform(clKey, { opacity: v }), captureSnapshot)}
                              {stepperRow('Z-Index', lt.zIndex ?? 0, -10, 100, 1, undefined, (v) => updateLayerTransform(clKey, { zIndex: v }), captureSnapshot)}
                            </div>
                          );
                        })()}

                        {/* ── Overlay / Logo position + size ── */}
                        {isValidOverlay && (() => {
                          const geo = getGeo(oKey);
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                              <div style={{ fontSize: 9, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 6, borderBottom: '1px solid #1e293b' }}>📐 Position &amp; Size</div>
                              {stepperRow('Position X', geo.x, 0, 1920, 1, 'px', (v) => { updateGeo(oKey, { x: v }); setSelectedOverlay(oKey); }, captureSnapshot)}
                              {stepperRow('Position Y', geo.y, 0, 1080, 1, 'px', (v) => { updateGeo(oKey, { y: v }); setSelectedOverlay(oKey); }, captureSnapshot)}
                              {stepperRow('Width', geo.w, 40, 1920, 1, 'px', (v) => { updateGeo(oKey, { w: Math.max(40, v) }); setSelectedOverlay(oKey); }, captureSnapshot)}
                              {stepperRow('Height', geo.h, 20, 300, 1, 'px', (v) => { updateGeo(oKey, { h: Math.max(20, v) }); setSelectedOverlay(oKey); }, captureSnapshot)}
                              {stepperRow('Font Size', geo.fontSize, 8, 80, 1, 'px', (v) => { updateGeo(oKey, { fontSize: Math.max(8, v) }); setSelectedOverlay(oKey); }, captureSnapshot)}
                              {stepperRow('Z-Index', geo.zIndex ?? ({'ticker':40,'topBar':20,'logo':30,'storyCounter':10,'breakingBanner':50} as Record<string,number>)[oKey as string] ?? 20, 0, 100, 1, undefined, (v) => { updateGeo(oKey, { zIndex: v }); setSelectedOverlay(oKey); }, captureSnapshot)}
                              <button onClick={() => {
                                if (!editConfig) return;
                                setEditConfig({ ...editConfig, overlayGeometry: { ...editConfig.overlayGeometry, [oKey]: { ...DEFAULT_OVERLAY_GEOMETRY[oKey] } } });
                              }} style={{ ...secondaryBtn, fontSize: 10, padding: '5px 0', width: '100%', marginTop: 2 }}>↺ Reset Position</button>

                              {/* ── Color & Background Image ── */}
                              {(() => {
                                const oc = editConfig.overlayColors?.[oKey as string] ?? {};
                                const setOC = (patch: Record<string, string | undefined>) =>
                                  setEditConfig({ ...editConfig, overlayColors: { ...editConfig.overlayColors, [oKey as string]: { ...oc, ...patch } } });
                                const hasBgImage = !!oc.bgImageUrl;
                                return (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, paddingTop: 8, borderTop: '1px solid #1e293b' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 4, borderBottom: '1px solid #1e293b' }}>🎨 Appearance</div>

                                    {/* Background: color or image toggle */}
                                    <div style={{ display: 'flex', gap: 4 }}>
                                      <button onClick={() => setOC({ bgImageUrl: undefined })}
                                        style={{ flex: 1, padding: '5px 0', fontSize: 10, borderRadius: 5, border: `1px solid ${!hasBgImage ? '#7c3aed' : '#1e293b'}`, background: !hasBgImage ? 'rgba(124,58,237,0.15)' : '#0f172a', color: !hasBgImage ? '#c4b5fd' : '#475569', cursor: 'pointer' }}>
                                        🎨 Color
                                      </button>
                                      <button onClick={() => {/* image mode — user uploads below */}}
                                        style={{ flex: 1, padding: '5px 0', fontSize: 10, borderRadius: 5, border: `1px solid ${hasBgImage ? '#7c3aed' : '#1e293b'}`, background: hasBgImage ? 'rgba(124,58,237,0.15)' : '#0f172a', color: hasBgImage ? '#c4b5fd' : '#475569', cursor: 'pointer' }}>
                                        🖼 Image
                                      </button>
                                    </div>

                                    {!hasBgImage && (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                          <span style={{ fontSize: 10, color: '#64748b', width: 62, flexShrink: 0 }}>BG Color</span>
                                          <input type="color" value={oc.bgColor ?? '#0d1f3c'}
                                            onChange={(e) => setOC({ bgImageUrl: undefined, bgColor: e.target.value })}
                                            style={{ width: 36, height: 28, border: '1px solid #1e293b', borderRadius: 4, background: 'none', cursor: 'pointer', padding: 2 }} />
                                          <span style={{ fontSize: 10, color: '#94a3b8' }}>{oc.bgColor ?? 'default'}</span>
                                          {oc.bgColor && <button onClick={() => setOC({ bgColor: undefined })} style={{ ...secondaryBtn, fontSize: 9, padding: '1px 6px' }}>↺</button>}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                          <span style={{ fontSize: 10, color: '#64748b', width: 62, flexShrink: 0 }}>Text Color</span>
                                          <input type="color" value={oc.textColor ?? '#ffffff'}
                                            onChange={(e) => setOC({ textColor: e.target.value })}
                                            style={{ width: 36, height: 28, border: '1px solid #1e293b', borderRadius: 4, background: 'none', cursor: 'pointer', padding: 2 }} />
                                          <span style={{ fontSize: 10, color: '#94a3b8' }}>{oc.textColor ?? 'default'}</span>
                                          {oc.textColor && <button onClick={() => setOC({ textColor: undefined })} style={{ ...secondaryBtn, fontSize: 9, padding: '1px 6px' }}>↺</button>}
                                        </div>
                                        {['ticker', 'topBar', 'storyCounter'].includes(oKey as string) && (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: 10, color: '#64748b', width: 62, flexShrink: 0 }}>Accent</span>
                                            <input type="color" value={oc.accentColor ?? '#f59e0b'}
                                              onChange={(e) => setOC({ accentColor: e.target.value })}
                                              style={{ width: 36, height: 28, border: '1px solid #1e293b', borderRadius: 4, background: 'none', cursor: 'pointer', padding: 2 }} />
                                            <span style={{ fontSize: 10, color: '#94a3b8' }}>{oc.accentColor ?? 'default'}</span>
                                            {oc.accentColor && <button onClick={() => setOC({ accentColor: undefined })} style={{ ...secondaryBtn, fontSize: 9, padding: '1px 6px' }}>↺</button>}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Background image upload */}
                                    <label style={{
                                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: 12,
                                      border: `2px dashed ${hasBgImage ? '#22c55e50' : '#1e293b'}`,
                                      borderRadius: 8, cursor: 'pointer', background: hasBgImage ? '#05250f40' : '#0a0f1e',
                                    }}>
                                      {hasBgImage
                                        ? <img src={oc.bgImageUrl} alt="" style={{ width: '100%', height: 50, objectFit: 'cover', borderRadius: 4 }} />
                                        : <div style={{ fontSize: 11, color: '#334155' }}>Upload background image</div>}
                                      <div style={{ fontSize: 10, color: hasBgImage ? '#22c55e' : '#7c3aed', fontWeight: 600 }}>{hasBgImage ? '↺ Replace image' : '＋ Upload'}</div>
                                      <input type="file" accept="image/*" style={{ display: 'none' }}
                                        onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          const url = URL.createObjectURL(file);
                                          setOC({ bgImageUrl: url, bgColor: undefined });
                                        }} />
                                    </label>
                                    {hasBgImage && (
                                      <button onClick={() => setOC({ bgImageUrl: undefined })} style={{ ...secondaryBtn, fontSize: 10, padding: '4px 0', width: '100%', color: '#ef4444', borderColor: '#ef444430' }}>🗑 Remove image</button>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })()}

                        {/* ── Video-specific toggles (in settings too) ── */}
                        {curLayer.key === 'video' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontSize: 9, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 6, borderBottom: '1px solid #1e293b' }}>🎬 Video Options</div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#0f172a', borderRadius: 7 }}>
                              <span style={{ fontSize: 11, color: '#94a3b8' }}>🔇 Mute audio</span>
                              <Toggle value={editConfig.videoMuted} onChange={() => setEditConfigWithHistory({ ...editConfig, videoMuted: !editConfig.videoMuted })} accentColor="#7c3aed" />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#0f172a', borderRadius: 7 }}>
                              <span style={{ fontSize: 11, color: '#94a3b8' }}>▶ Prefer video over image</span>
                              <Toggle value={editConfig.preferVideo} onChange={() => setEditConfigWithHistory({ ...editConfig, preferVideo: !editConfig.preferVideo })} accentColor="#7c3aed" />
                            </div>
                          </div>
                        )}

                        {/* ── Highlight words: no extra settings beyond toggle ── */}
                        {curLayer.key === 'highlightWords' && (
                          <div style={{ fontSize: 11, color: '#475569', textAlign: 'center', padding: '8px 0', lineHeight: 1.5 }}>
                            Add highlight words in the Content tab after selecting this layer.
                          </div>
                        )}

                        {/* ── Audio: no positional settings, just note ── */}
                        {curLayer.group === 'Audio' && (
                          <div style={{ fontSize: 11, color: '#475569', padding: '8px 10px', background: '#0a0f1e', borderRadius: 7, lineHeight: 1.5 }}>
                            Upload audio in the Content tab. Toggle on/off above to enable in the preview.
                          </div>
                        )}
                      </div>
                    );
                  }

                  // ════════════════════════════════
                  // CONTENT TAB
                  // ════════════════════════════════
                  if (rightTab === 'content') {
                    if (!curLayer) {
                      return (
                        <div style={{ fontSize: 11, color: '#334155', textAlign: 'center', padding: 28, lineHeight: 1.6 }}>
                          ← Select a layer from the Layers tab to edit its content
                        </div>
                      );
                    }

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {/* Layer identity chip */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#0f172a', borderRadius: 7 }}>
                          <span style={{ fontSize: 16 }}>{curLayer.icon}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#c4b5fd' }}>{curLayer.label}</span>
                        </div>

                        {/* Media upload */}
                        {curLayer.group === 'Media' && curLayer.assetKey && (() => {
                          const a = prevAssets[curLayer.assetKey];
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              <label style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                gap: 8, padding: 16, border: `2px dashed ${a ? '#22c55e50' : '#1e293b'}`,
                                borderRadius: 10, cursor: 'pointer', background: a ? '#05250f40' : '#0a0f1e', minHeight: 90,
                              }}>
                                {a && a.type === 'image' && <img src={a.url} alt="" style={{ width: '100%', maxHeight: 130, objectFit: 'cover', borderRadius: 6 }} />}
                                {a && a.type === 'video' && <video src={a.url} style={{ width: '100%', maxHeight: 130, objectFit: 'cover', borderRadius: 6 }} muted />}
                                {a && a.type === 'audio' && <><div style={{ fontSize: 28 }}>🎵</div><div style={{ fontSize: 11, color: '#22c55e' }}>{a.name}</div></>}
                                {!a && <><div style={{ fontSize: 28 }}>{curLayer.icon}</div><div style={{ fontSize: 11, color: '#475569' }}>Click to upload</div><div style={{ fontSize: 9, color: '#334155' }}>{curLayer.assetAccept}</div></>}
                                <div style={{ fontSize: 10, color: a ? '#22c55e' : '#7c3aed', fontWeight: 600 }}>{a ? '↺ Replace file' : '＋ Upload'}</div>
                                <input type="file" accept={curLayer.assetAccept} style={{ display: 'none' }}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file || !curLayer.assetKey) return;
                                    const url = URL.createObjectURL(file);
                                    const uploaded: UploadedAsset = { id: crypto.randomUUID(), key: 'primary', name: file.name, url, type: file.type.startsWith('video') ? 'video' : file.type.startsWith('audio') ? 'audio' : 'image', size: file.size, mimeType: file.type, uploadedAt: new Date().toISOString() };
                                    setPrevAssets((p) => ({ ...p, [curLayer.assetKey!]: uploaded }));
                                  }}
                                />
                              </label>
                              {a && <div style={{ fontSize: 10, color: '#475569', textAlign: 'center' }}>{a.name}</div>}
                              {curLayer.key === 'video' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#0f172a', borderRadius: 7 }}>
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>🔇 Mute video audio</span>
                                    <Toggle value={editConfig.videoMuted} onChange={() => setEditConfigWithHistory({ ...editConfig, videoMuted: !editConfig.videoMuted })} accentColor="#7c3aed" />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#0f172a', borderRadius: 7 }}>
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>▶ Prefer video over image</span>
                                    <Toggle value={editConfig.preferVideo} onChange={() => setEditConfigWithHistory({ ...editConfig, preferVideo: !editConfig.preferVideo })} accentColor="#7c3aed" />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Audio upload */}
                        {curLayer.group === 'Audio' && curLayer.assetKey && (() => {
                          const a = prevAssets[curLayer.assetKey];
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              <label style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                gap: 8, padding: 20, border: `2px dashed ${a ? '#22c55e50' : '#1e293b'}`,
                                borderRadius: 10, cursor: 'pointer', background: '#0a0f1e', minHeight: 100,
                              }}>
                                <div style={{ fontSize: 28 }}>{a ? '🎵' : curLayer.icon}</div>
                                {a ? <div style={{ fontSize: 11, color: '#22c55e' }}>{a.name}</div> : <div style={{ fontSize: 11, color: '#475569' }}>Click to upload audio</div>}
                                <div style={{ fontSize: 10, color: a ? '#22c55e' : '#7c3aed', fontWeight: 600 }}>{a ? '↺ Replace' : '＋ Upload'}</div>
                                <input type="file" accept={curLayer.assetAccept} style={{ display: 'none' }}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file || !curLayer.assetKey) return;
                                    const url = URL.createObjectURL(file);
                                    const uploaded: UploadedAsset = { id: crypto.randomUUID(), key: 'primary', name: file.name, url, type: 'audio', size: file.size, mimeType: file.type, uploadedAt: new Date().toISOString() };
                                    setPrevAssets((p) => ({ ...p, [curLayer.assetKey!]: uploaded }));
                                  }}
                                />
                              </label>
                              {a && <audio src={a.url} controls style={{ width: '100%' }} />}
                            </div>
                          );
                        })()}

                        {/* Text content */}
                        {curLayer.group === 'Text' && (() => {
                          const textKey = curLayer.key as keyof typeof prevText;
                          const hasTextKey = textKey in prevText;
                          const curStyle = textLayerStyle[curLayer.key] ?? {};
                          const isHeadline = curLayer.key === 'headline';
                          const isSubheadline = curLayer.key === 'subheadline';
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                              {hasTextKey && (
                                <div>
                                  <div style={{ fontSize: 9, color: '#475569', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Content</div>
                                  <textarea
                                    value={(prevText as Record<string, string>)[textKey] ?? ''}
                                    onChange={(e) => setPrevText((p) => ({ ...p, [textKey]: e.target.value }))}
                                    rows={4}
                                    style={{ ...inputStyle, fontSize: 12, resize: 'vertical', lineHeight: 1.5 }}
                                    placeholder={`Enter ${curLayer.label.toLowerCase()}…`}
                                  />
                                </div>
                              )}
                              {(isHeadline || isSubheadline) && (
                                <>
                                  <div>
                                    <div style={{ fontSize: 9, color: '#475569', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Font Size</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <input type="range" min={20} max={120} step={2}
                                        value={curStyle.fontSize ?? (isHeadline ? headlineFont.fontSize : subheadlineFont.fontSize)}
                                        onChange={(e) => setTextLayerStyle((p) => ({ ...p, [curLayer.key]: { ...p[curLayer.key], fontSize: Number(e.target.value) } }))}
                                        style={{ flex: 1, accentColor: '#7c3aed' } as React.CSSProperties} />
                                      <span style={{ fontSize: 12, color: '#94a3b8', width: 40 }}>{curStyle.fontSize ?? (isHeadline ? headlineFont.fontSize : subheadlineFont.fontSize)}px</span>
                                    </div>
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 9, color: '#475569', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Alignment</div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                      {(['left', 'center', 'right'] as const).map((a) => (
                                        <button key={a} onClick={() => setTextLayerStyle((p) => ({ ...p, [curLayer.key]: { ...p[curLayer.key], align: a } }))}
                                          style={{ flex: 1, padding: '6px 0', fontSize: 14, background: curStyle.align === a ? '#7c3aed' : '#0f172a', border: `1px solid ${curStyle.align === a ? '#7c3aed' : '#1e293b'}`, borderRadius: 5, color: '#fff', cursor: 'pointer' }}>
                                          {a === 'left' ? '⬅' : a === 'center' ? '⬛' : '➡'}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </>
                              )}
                              {!hasTextKey && !isHeadline && !isSubheadline && (
                                <div style={{ fontSize: 11, color: '#334155', textAlign: 'center', padding: 12 }}>
                                  Set content via the main story text fields.
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Overlay text content */}
                        {curLayer.group === 'Overlays' && (() => {
                          const textPropKey = curLayer.key === 'ticker' ? 'tickerText' : curLayer.key === 'topBar' ? 'topBarText' : null;
                          if (!textPropKey) {
                            return <div style={{ fontSize: 11, color: '#334155', textAlign: 'center', padding: 12 }}>No editable content for this overlay.</div>;
                          }
                          return (
                            <div>
                              <div style={{ fontSize: 9, color: '#475569', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Content</div>
                              <input
                                value={(prevText as Record<string, string>)[textPropKey]}
                                onChange={(e) => setPrevText((p) => ({ ...p, [textPropKey!]: e.target.value }))}
                                style={{ ...inputStyle, fontSize: 12 }}
                                placeholder={`${curLayer.label} text…`}
                              />
                            </div>
                          );
                        })()}
                      </div>
                    );
                  }

                  return null;
                })()}
              </div>

              {/* ── Fixed bottom: action buttons ── */}
              <div style={{ padding: '10px 14px', borderTop: '1px solid #1e293b', display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                <button onClick={handleSave} style={{ ...primaryBtn, background: savedFlash ? '#22c55e' : '#7c3aed', padding: '9px 16px', fontSize: 12, transition: 'background 0.3s' }}>
                  {savedFlash ? '✓ Saved!' : '💾 Save Config'}
                </button>
                <button onClick={handleReset} style={{ ...secondaryBtn, padding: '7px 16px', fontSize: 11 }}>↺ Reset to Default</button>
                {editConfig.isCustom && (
                  <button onClick={handleDelete} style={{ ...secondaryBtn, padding: '7px 16px', fontSize: 11, color: '#ef4444', borderColor: '#ef444440' }}>🗑 Delete Custom</button>
                )}
              </div>
            </>
          ) : (
            <div style={{ padding: 24, color: '#334155', textAlign: 'center', fontSize: 12 }}>Select a layout to edit</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Story List Item ──────────────────────────────────────────

const StoryListItem: React.FC<{
  story: Story; accentColor: string; selected: boolean; onSelect: () => void;
}> = ({ story, accentColor, selected, onSelect }) => (
  <div
    onClick={onSelect}
    style={{
      padding: '10px 14px', cursor: 'pointer',
      background: selected ? `rgba(${hexToRgb(accentColor)},0.12)` : 'transparent',
      borderLeft: selected ? `3px solid ${accentColor}` : '3px solid transparent',
      borderBottom: '1px solid #0f172a',
      transition: 'all 0.1s',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: selected ? accentColor : '#475569', background: `rgba(${hexToRgb(accentColor)},0.15)`, padding: '1px 6px', borderRadius: 4 }}>
        #{String(story.index).padStart(2, '0')}
      </div>
      {story.isBreaking && (
        <div style={{ fontSize: 8, background: '#dc2626', color: '#fff', padding: '1px 5px', borderRadius: 3, letterSpacing: 1, fontWeight: 800 }}>LIVE</div>
      )}
    </div>
    <div style={{ fontSize: 12, color: selected ? '#e2e8f0' : '#94a3b8', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 4 }}>
      {story.headline || 'Untitled Story'}
    </div>
    <div style={{ fontSize: 10, color: '#334155' }}>
      {story.scenes.length} scene{story.scenes.length !== 1 ? 's' : ''} · {story.duration}s
    </div>
  </div>
);

// ─── Scene Viewer ─────────────────────────────────────────────

const SceneViewer: React.FC<{
  story: Story; selectedSceneId: string;
  onSelectScene: (id: string) => void; accentColor: string;
}> = ({ story, selectedSceneId, onSelectScene, accentColor }) => (
  <div>
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        {story.isBreaking && <div style={{ fontSize: 10, background: '#dc2626', color: '#fff', padding: '2px 8px', borderRadius: 4, fontWeight: 800, letterSpacing: 1 }}>BREAKING</div>}
        <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: '#e2e8f0' }}>{story.headline}</h2>
      </div>
      {story.subheadline && <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{story.subheadline}</p>}
      <div style={{ fontSize: 11, color: '#334155', marginTop: 6 }}>{story.scenes.length} auto-generated scene{story.scenes.length !== 1 ? 's' : ''}</div>
    </div>

    {story.scenes.length === 0 ? (
      <div style={{ padding: 32, textAlign: 'center', color: '#334155', fontSize: 13 }}>
        No scenes generated — add a headline and regenerate.
      </div>
    ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 14 }}>
        {story.scenes.map((scene, i) => (
          <SceneCard
            key={scene.id}
            scene={scene}
            index={i + 1}
            selected={scene.id === selectedSceneId}
            onSelect={() => onSelectScene(scene.id)}
            accentColor={accentColor}
          />
        ))}
      </div>
    )}
  </div>
);

// Layout wireframe icons (simplified SVG previews per layout)
function layoutWireframe(layoutId: string): React.ReactNode {
  const s = { stroke: '#334155', fill: 'none', strokeWidth: 1.5 };
  switch (layoutId) {
    case 'L1': return <svg viewBox="0 0 80 45" width="80" height="45"><rect x="0" y="0" width="80" height="45" fill="#0f1e35"/><rect x="0" y="0" width="80" height="45" {...s} opacity={0.5}/><rect x="8" y="30" width="50" height="6" fill="#334155" rx="1"/><rect x="8" y="38" width="35" height="3" fill="#1e293b" rx="1"/></svg>;
    case 'L2': return <svg viewBox="0 0 80 45" width="80" height="45"><rect x="0" y="0" width="80" height="45" fill="#0f1e35"/><rect x="0" y="0" width="44" height="45" fill="#111f35"/><rect x="46" y="8" width="28" height="6" fill="#334155" rx="1"/><rect x="46" y="18" width="22" height="4" fill="#1e293b" rx="1"/></svg>;
    case 'L3': return <svg viewBox="0 0 80 45" width="80" height="45"><rect x="0" y="0" width="80" height="45" fill="#0f1e35"/><rect x="36" y="0" width="44" height="45" fill="#111f35"/><rect x="6" y="8" width="28" height="6" fill="#334155" rx="1"/><rect x="6" y="18" width="22" height="4" fill="#1e293b" rx="1"/></svg>;
    case 'L4': return <svg viewBox="0 0 80 45" width="80" height="45"><rect x="0" y="0" width="80" height="45" fill="#0f1e35"/><rect x="0" y="0" width="38" height="32" fill="#111f35"/><rect x="42" y="0" width="38" height="32" fill="#111f35"/><rect x="8" y="34" width="50" height="6" fill="#334155" rx="1"/></svg>;
    case 'L5': return <svg viewBox="0 0 80 45" width="80" height="45"><rect x="0" y="0" width="80" height="45" fill="#0a1628"/><rect x="10" y="14" width="60" height="8" fill="#1e3a8a" rx="2"/><rect x="20" y="26" width="40" height="5" fill="#1e293b" rx="1"/></svg>;
    case 'L6': return <svg viewBox="0 0 80 45" width="80" height="45"><rect x="0" y="0" width="80" height="45" fill="#0f1e35"/><rect x="0" y="0" width="80" height="32" fill="#111f35"/><rect x="0" y="32" width="80" height="13" fill="#0a1628"/><rect x="8" y="35" width="50" height="5" fill="#334155" rx="1"/></svg>;
    case 'L7': return <svg viewBox="0 0 80 45" width="80" height="45"><rect x="0" y="0" width="80" height="45" fill="#0a1628"/><text x="40" y="26" textAnchor="middle" style={{ fill: '#e8c84a', fontSize: 16, fontWeight: 800 }}>₹5K</text></svg>;
    case 'L8': return <svg viewBox="0 0 80 45" width="80" height="45"><rect x="0" y="0" width="80" height="45" fill="#0a1628"/><rect x="4" y="4" width="24" height="24" fill="#111f35" rx="3"/><rect x="32" y="8" width="44" height="5" fill="#334155" rx="1"/><rect x="32" y="16" width="36" height="4" fill="#1e293b" rx="1"/></svg>;
    case 'L10': return <svg viewBox="0 0 80 45" width="80" height="45"><rect x="0" y="0" width="80" height="45" fill="#0f1e35"/><rect x="2" y="2" width="36" height="20" fill="#111f35" rx="1"/><rect x="42" y="2" width="36" height="20" fill="#111f35" rx="1"/><rect x="2" y="25" width="36" height="18" fill="#111f35" rx="1"/><rect x="42" y="25" width="36" height="18" fill="#111f35" rx="1"/></svg>;
    case 'L12': return <svg viewBox="0 0 80 45" width="80" height="45"><rect x="0" y="0" width="80" height="45" fill="#0a1628"/><rect x="8" y="10" width="30" height="7" fill="#1e3a8a" rx="1"/><rect x="8" y="21" width="55" height="7" fill="#334155" rx="1"/><rect x="8" y="32" width="40" height="7" fill="#1e3a8a" rx="1"/></svg>;
    default: return <svg viewBox="0 0 80 45" width="80" height="45"><rect x="0" y="0" width="80" height="45" fill="#0f172a"/><rect x="10" y="16" width="60" height="13" fill="#1e293b" rx="2"/></svg>;
  }
}

const SceneCard: React.FC<{
  scene: Scene; index: number; selected: boolean;
  onSelect: () => void; accentColor: string;
}> = ({ scene, index, selected, onSelect, accentColor }) => (
  <div
    onClick={onSelect}
    style={{
      background: '#0f172a', border: `1.5px solid ${selected ? accentColor : '#1e293b'}`,
      borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
      boxShadow: selected ? `0 0 16px rgba(${hexToRgb(accentColor)},0.25)` : 'none',
      transition: 'all 0.15s',
    }}
  >
    {/* Wireframe preview */}
    <div style={{ background: '#060b14', padding: '12px', display: 'flex', justifyContent: 'center' }}>
      {layoutWireframe(scene.layoutId)}
    </div>
    {/* Info */}
    <div style={{ padding: '10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: '#475569', letterSpacing: 0.5 }}>SCENE {index}</div>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 9, fontWeight: 700, color: accentColor, background: `rgba(${hexToRgb(accentColor)},0.15)`, padding: '1px 6px', borderRadius: 4, letterSpacing: 0.5 }}>
          {scene.layoutId}
        </div>
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 2 }}>
        {LAYOUT_LABELS[scene.layoutId] ?? scene.layoutId}
      </div>
      <div style={{ fontSize: 10, color: '#475569' }}>
        {scene.duration.toFixed(1)}s · {scene.transitionIn ?? 'cut'}
      </div>
    </div>
  </div>
);

// ─── Story Properties Panel ───────────────────────────────────

const StoryPropertiesPanel: React.FC<{ story: Story; accentColor: string }> = ({ story, accentColor }) => (
  <div style={{ padding: 16 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 }}>
      Story Properties
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <PropRow label="Index" value={`#${String(story.index).padStart(2, '0')}`} accent={accentColor} />
      <PropRow label="Mode" value={story.sceneMode === 'auto' ? 'Auto' : 'Manual'} />
      <PropRow label="Duration" value={`${story.duration}s`} />
      <PropRow label="Scenes" value={String(story.scenes.length)} />
      {story.isBreaking && <PropRow label="Status" value="BREAKING" accent="#dc2626" />}
      {story.bigNumber && <PropRow label="Big Number" value={story.bigNumber} />}
      {story.tickerText && (
        <div>
          <div style={{ fontSize: 10, color: '#475569', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Ticker</div>
          <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.4 }}>{story.tickerText}</div>
        </div>
      )}
      {story.script && (
        <div>
          <div style={{ fontSize: 10, color: '#475569', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Script</div>
          <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>{story.script}</div>
        </div>
      )}
    </div>

    {/* Scene summary */}
    {story.scenes.length > 0 && (
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #1e293b' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Scene Sequence</div>
        {story.scenes.map((scene, i) => (
          <div key={scene.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ width: 22, height: 22, borderRadius: 5, background: `rgba(${hexToRgb(accentColor)},0.15)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: accentColor, flexShrink: 0 }}>
              {i + 1}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8' }}>{scene.layoutId}</div>
              <div style={{ fontSize: 9, color: '#475569' }}>{scene.duration.toFixed(1)}s</div>
            </div>
            <div style={{ fontSize: 9, color: '#334155', textAlign: 'right' }}>
              {LAYOUT_LABELS[scene.layoutId]?.split(' ').slice(0, 2).join(' ')}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const PropRow: React.FC<{ label: string; value: string; accent?: string }> = ({ label, value, accent }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ fontSize: 11, color: '#475569', fontWeight: 500 }}>{label}</span>
    <span style={{ fontSize: 11, color: accent ?? '#94a3b8', fontWeight: 700 }}>{value}</span>
  </div>
);

// ─── Toggle switch ─────────────────────────────────────────────

const Toggle: React.FC<{ value: boolean; onChange: (v: boolean) => void; accentColor?: string }> = ({ value, onChange, accentColor = '#3b82f6' }) => (
  <button
    onClick={() => onChange(!value)}
    style={{
      width: 36, height: 20, borderRadius: 10, padding: 0, border: 'none', cursor: 'pointer',
      background: value ? accentColor : '#1e293b', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
    }}
  >
    <div style={{
      width: 14, height: 14, borderRadius: '50%', background: '#fff',
      position: 'absolute', top: 3, left: value ? 19 : 3, transition: 'left 0.2s',
    }} />
  </button>
);

// ─── Scene Prop Editor (right panel "Scene" tab) ───────────────

const LAYOUT_IDS: SceneLayoutId[] = ['L1','L2','L3','L4','L5','L6','L7','L8','L10','L12'];

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 9, fontWeight: 800, color: '#334155', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8, marginTop: 4, paddingBottom: 5, borderBottom: '1px solid #1e293b' }}>
    {children}
  </div>
);

const AssetRow: React.FC<{ icon: string; name: string; sub?: string; children?: React.ReactNode }> = ({ icon, name, sub, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#0a0f1e', borderRadius: 7, border: '1px solid #1e293b' }}>
    <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
    <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
      <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{name}</div>
      {sub && <div style={{ fontSize: 9, color: '#334155' }}>{sub}</div>}
    </div>
    {children}
  </div>
);

const ScenePropEditor: React.FC<{
  scene: Scene;
  accentColor: string;
  onSave: (scene: Scene) => void;
}> = ({ scene, accentColor, onSave }) => {
  const p = scene.props;
  const [headline, setHeadline] = useState(p.headline);
  const [subheadline, setSubheadline] = useState(p.subheadline ?? '');
  const [layoutId, setLayoutId] = useState<SceneLayoutId>(p.layoutId);
  const [videoMuted, setVideoMuted] = useState(p.videoMuted ?? false);
  const [hideImage, setHideImage] = useState(p.hideImage ?? false);
  const [hideVideo, setHideVideo] = useState(p.hideVideo ?? false);
  const [voiceoverVolume, setVoiceoverVolume] = useState(p.voiceoverVolume ?? 1.0);
  const [bgMusicVolume, setBgMusicVolume] = useState(p.bgMusicVolume ?? 0.3);
  const [duration, setDuration] = useState(scene.duration);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setHeadline(p.headline);
    setSubheadline(p.subheadline ?? '');
    setLayoutId(p.layoutId);
    setVideoMuted(p.videoMuted ?? false);
    setHideImage(p.hideImage ?? false);
    setHideVideo(p.hideVideo ?? false);
    setVoiceoverVolume(p.voiceoverVolume ?? 1.0);
    setBgMusicVolume(p.bgMusicVolume ?? 0.3);
    setDuration(scene.duration);
    setSaved(false);
  }, [scene.id]);

  const handleApply = () => {
    onSave({
      ...scene,
      layoutId,
      duration,
      props: { ...p, headline, subheadline: subheadline || undefined, layoutId, videoMuted, hideImage, hideVideo, voiceoverVolume, bgMusicVolume },
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  // Collect assets from scene props
  const assets = p.assets ?? {};
  const hasImages = !!(assets.primary?.type === 'image' || assets.secondary?.type === 'image' || (assets.images?.length ?? 0) > 0);
  const hasVideo = !!(assets.primary?.type === 'video' || assets.video);
  const hasVoiceover = !!p.voiceoverUrl;

  const imageList = [
    assets.primary?.type === 'image' ? { url: assets.primary.url, name: assets.primary.name, role: 'Main' } : null,
    assets.secondary?.type === 'image' ? { url: assets.secondary.url, name: assets.secondary.name, role: 'Secondary' } : null,
    ...(assets.images ?? []).map((img, i) => ({ url: img.url, name: img.name, role: `Grid ${i + 1}` })),
  ].filter(Boolean) as { url: string; name: string; role: string }[];

  const videoAsset = assets.primary?.type === 'video' ? assets.primary : assets.video;

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Scene info strip */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 10, background: '#1e293b', color: '#94a3b8', padding: '2px 7px', borderRadius: 4, fontWeight: 700 }}>{scene.layoutId}</span>
        <span style={{ fontSize: 10, color: '#334155' }}>{scene.duration}s</span>
        <span style={{ fontSize: 10, color: '#334155' }}>·</span>
        <span style={{ fontSize: 10, color: '#475569' }}>{LAYOUT_LABELS[scene.layoutId]}</span>
      </div>

      {/* ── IMAGES ─────────────────────────────────── */}
      {hasImages && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
            <SectionLabel>🖼 Images</SectionLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9 }}>
              <span style={{ fontSize: 10, color: hideImage ? '#ef4444' : '#22c55e' }}>{hideImage ? 'Hidden' : 'Visible'}</span>
              <Toggle value={!hideImage} onChange={(v) => setHideImage(!v)} accentColor={accentColor} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, opacity: hideImage ? 0.4 : 1, transition: 'opacity 0.2s' }}>
            {imageList.map((img) => (
              <AssetRow key={img.url} icon="🖼" name={img.name} sub={img.role}>
                <img src={img.url} alt="" style={{ width: 36, height: 24, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />
              </AssetRow>
            ))}
          </div>
        </div>
      )}

      {/* ── VIDEO ──────────────────────────────────── */}
      {hasVideo && videoAsset && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
            <SectionLabel>🎬 Video</SectionLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9 }}>
              <span style={{ fontSize: 10, color: hideVideo ? '#ef4444' : '#22c55e' }}>{hideVideo ? 'Hidden' : 'Visible'}</span>
              <Toggle value={!hideVideo} onChange={(v) => setHideVideo(!v)} accentColor={accentColor} />
            </div>
          </div>
          <div style={{ opacity: hideVideo ? 0.4 : 1, transition: 'opacity 0.2s' }}>
            <AssetRow icon="🎬" name={videoAsset.name} sub={`${(videoAsset.size / 1024 / 1024).toFixed(1)} MB`}>
              <span style={{ fontSize: 9, color: '#475569', flexShrink: 0 }}>clip</span>
            </AssetRow>
            {/* Video sound mute */}
            {!hideVideo && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, padding: '7px 10px', background: '#0a0f1e', borderRadius: 7, border: '1px solid #1e293b' }}>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Video sound</div>
                  <div style={{ fontSize: 9, color: '#334155' }}>{videoMuted ? '🔇 Muted' : '🔊 Playing'}</div>
                </div>
                <Toggle value={!videoMuted} onChange={(v) => setVideoMuted(!v)} accentColor={accentColor} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── VOICEOVER ──────────────────────────────── */}
      {hasVoiceover && (
        <div>
          <SectionLabel>🎙 Voiceover</SectionLabel>
          <AssetRow icon="🎙" name={p.voiceoverUrl!.split('/').pop() ?? 'voiceover'} sub="Story narration" />
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 10, color: '#64748b' }}>Volume</span>
              <span style={{ fontSize: 10, color: '#475569', fontWeight: 700 }}>{Math.round(voiceoverVolume * 100)}%</span>
            </div>
            <input type="range" min={0} max={1} step={0.05} value={voiceoverVolume}
              onChange={(e) => setVoiceoverVolume(Number(e.target.value))}
              style={{ width: '100%', accentColor }} />
          </div>
        </div>
      )}

      {/* ── BG MUSIC ───────────────────────────────── */}
      <div>
        <SectionLabel>🎵 BG Music</SectionLabel>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 10, color: '#64748b' }}>Volume</span>
          <span style={{ fontSize: 10, color: '#475569', fontWeight: 700 }}>{Math.round(bgMusicVolume * 100)}%</span>
        </div>
        <input type="range" min={0} max={1} step={0.05} value={bgMusicVolume}
          onChange={(e) => setBgMusicVolume(Number(e.target.value))}
          style={{ width: '100%', accentColor }} />
        <div style={{ fontSize: 9, color: '#334155', marginTop: 3 }}>Plays continuously across all scenes</div>
      </div>

      {/* ── LAYOUT ─────────────────────────────────── */}
      <div>
        <SectionLabel>📐 Layout</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 4, marginBottom: 5 }}>
          {LAYOUT_IDS.map((id) => (
            <button key={id} onClick={() => setLayoutId(id)} style={{
              background: layoutId === id ? `rgba(${hexToRgb(accentColor)},0.2)` : '#0f172a',
              border: `1px solid ${layoutId === id ? accentColor : '#1e293b'}`,
              borderRadius: 6, padding: '5px 2px', cursor: 'pointer',
              color: layoutId === id ? accentColor : '#64748b',
              fontSize: 10, fontWeight: 700, textAlign: 'center',
            }}>{id}</button>
          ))}
        </div>
        <div style={{ fontSize: 10, color: '#475569' }}>{LAYOUT_LABELS[layoutId]}</div>
      </div>

      {/* ── TEXT ───────────────────────────────────── */}
      <div>
        <SectionLabel>✍️ Text</SectionLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>
            <div style={{ fontSize: 9, color: '#475569', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>Headline</div>
            <textarea value={headline} onChange={(e) => setHeadline(e.target.value)}
              rows={2} style={{ ...inputStyle, fontSize: 12, resize: 'vertical', lineHeight: 1.4 }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: '#475569', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>Subheadline</div>
            <textarea value={subheadline} onChange={(e) => setSubheadline(e.target.value)}
              rows={2} style={{ ...inputStyle, fontSize: 12, resize: 'vertical', lineHeight: 1.4 }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: '#475569', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>Duration</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="range" min={2} max={30} step={0.5} value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                style={{ flex: 1, accentColor }} />
              <span style={{ fontSize: 11, color: '#94a3b8', width: 32, textAlign: 'right' }}>{duration}s</span>
            </div>
          </div>
        </div>
      </div>

      {/* Apply */}
      <button
        onClick={handleApply}
        style={{
          ...primaryBtn, background: saved ? '#22c55e' : accentColor,
          width: '100%', padding: '9px 16px', fontSize: 12, transition: 'background 0.3s',
        }}
      >
        {saved ? '✓ Saved!' : 'Apply Changes'}
      </button>
    </div>
  );
};

// ─── Show Settings Panel (right panel "Settings" tab) ─────────

const ShowSettingsPanel: React.FC<{
  project: ShowProject;
  onChange: (p: ShowProject) => void;
  accentColor: string;
}> = ({ project, onChange, accentColor }) => {
  const overlay = project.showTemplate.globalOverlay;

  const toggleOverlay = (key: keyof typeof overlay, val: boolean) => {
    onChange({
      ...project,
      showTemplate: {
        ...project.showTemplate,
        globalOverlay: { ...overlay, [key]: val },
      },
    });
  };

  const panelLabelStyle: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, color: '#475569',
    letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10,
  };

  const overlaySettings = [
    { key: 'ticker' as const,       label: 'News Ticker',    icon: '📰', desc: 'Bottom scrolling ticker' },
    { key: 'logo' as const,         label: 'Channel Logo',   icon: '🏷️', desc: 'Logo watermark' },
    { key: 'topBar' as const,       label: 'Top Bar',        icon: '📊', desc: 'Show name top bar' },
    { key: 'storyCounter' as const, label: 'Story Counter',  icon: '🔢', desc: 'Story number label' },
    { key: 'lowerThird' as const,   label: 'Lower Third',    icon: '📋', desc: 'Reporter/location bar' },
    { key: 'breakingBannerOnBreaking' as const, label: 'Breaking Banner', icon: '🔴', desc: 'Red banner on breaking' },
  ];

  return (
    <div style={{ padding: 14 }}>
      {/* Overlays */}
      <div style={panelLabelStyle}>Overlays</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 20 }}>
        {overlaySettings.map((s) => (
          <div key={s.key} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 10px', borderRadius: 8,
            background: overlay[s.key] ? `rgba(${hexToRgb(accentColor)},0.07)` : '#0f172a',
            border: `1px solid ${overlay[s.key] ? accentColor + '40' : '#1e293b'}`,
          }}>
            <div>
              <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                <span>{s.icon}</span>{s.label}
              </div>
              <div style={{ fontSize: 10, color: '#334155' }}>{s.desc}</div>
            </div>
            <Toggle value={overlay[s.key] as boolean} onChange={(v) => toggleOverlay(s.key, v)} accentColor={accentColor} />
          </div>
        ))}
      </div>

      {/* Ticker speed */}
      {overlay.ticker && (
        <div style={{ marginBottom: 16 }}>
          <div style={panelLabelStyle}>Ticker Speed</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="range" min={50} max={400} step={10} value={overlay.tickerSpeed}
              onChange={(e) => onChange({
                ...project,
                showTemplate: { ...project.showTemplate, globalOverlay: { ...overlay, tickerSpeed: Number(e.target.value) } }
              })}
              style={{ flex: 1, accentColor }} />
            <span style={{ fontSize: 11, color: '#94a3b8', width: 36 }}>{overlay.tickerSpeed}</span>
          </div>
        </div>
      )}

      {/* Audio volumes */}
      <div style={panelLabelStyle}>Audio</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[
          { label: 'BG Music', key: 'bgMusicVolume' as const, value: project.bgMusicVolume ?? 0.3 },
          { label: 'Voiceover', key: 'voiceoverVolume' as const, value: project.voiceoverVolume ?? 0.9 },
        ].map((row) => (
          <div key={row.key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: '#64748b' }}>{row.label}</span>
              <span style={{ fontSize: 11, color: '#475569' }}>{Math.round(row.value * 100)}%</span>
            </div>
            <input type="range" min={0} max={1} step={0.05} value={row.value}
              onChange={(e) => onChange({ ...project, [row.key]: Number(e.target.value) })}
              style={{ width: '100%', accentColor }} />
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Master Timeline Bar ──────────────────────────────────────

const MasterTimeline: React.FC<{
  project: ShowProject;
  timeline: ReturnType<typeof buildShowTimeline>;
  selectedStoryId: string;
  onSelectStory: (id: string) => void;
  showType: ShowTypeItem;
  onBack: () => void;
}> = ({ project, timeline, selectedStoryId, onSelectStory, onBack }) => {
  const totalSec = timeline.totalDurationSec;
  const items = timeline.timeline;

  // Assign a color per story
  const storyColors = project.stories.reduce<Record<string, string>>((acc, s, i) => {
    const hues = [210, 160, 280, 30, 340, 120, 60, 190, 300];
    acc[s.id] = `hsl(${hues[i % hues.length]},70%,45%)`;
    return acc;
  }, {});

  return (
    <div style={{ height: 72, background: '#0d1117', borderTop: '1px solid #1e293b', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
      {/* Timeline header row */}
      <div style={{ height: 24, display: 'flex', alignItems: 'center', padding: '0 14px', borderBottom: '1px solid #0f172a', gap: 12 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: '#334155', letterSpacing: 1, textTransform: 'uppercase' }}>
          Master Timeline · {totalSec.toFixed(1)}s · {timeline.totalDurationFrames} frames
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={onBack} style={{ ...secondaryBtn, padding: '2px 10px', fontSize: 10 }}>← Back to Stories</button>
      </div>

      {/* Timeline track */}
      <div style={{ flex: 1, padding: '6px 14px', overflow: 'hidden' }}>
        <div style={{ height: '100%', display: 'flex', gap: 1, position: 'relative', borderRadius: 4, overflow: 'hidden' }}>
          {items.map((item) => {
            const pct = ((item.endSec - item.startSec) / totalSec) * 100;
            const isScene = item.type === 'scene';
            const isCounter = item.type === 'story-counter';
            const isTransition = item.type === 'transition';
            const isIntroOutro = item.type === 'intro' || item.type === 'outro';
            const storyColor = item.storyId ? storyColors[item.storyId] : '#334155';
            const isSelected = item.storyId === selectedStoryId;

            return (
              <div
                key={item.id}
                onClick={() => item.storyId && onSelectStory(item.storyId)}
                title={item.type + (item.storyId ? ` · Story #${project.stories.find((s) => s.id === item.storyId)?.index}` : '')}
                style={{
                  width: `${pct}%`, minWidth: 1, height: '100%',
                  background: isIntroOutro
                    ? '#1e293b'
                    : isTransition
                    ? '#0f172a'
                    : isCounter
                    ? `${storyColor}60`
                    : storyColor,
                  opacity: isScene ? (isSelected ? 1 : 0.55) : 0.7,
                  cursor: item.storyId ? 'pointer' : 'default',
                  borderRadius: 2,
                  border: isSelected && isScene ? `1px solid ${storyColor}` : '1px solid transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                  transition: 'opacity 0.15s',
                }}
              >
                {isIntroOutro && pct > 3 && (
                  <div style={{ fontSize: 8, fontWeight: 700, color: '#64748b', letterSpacing: 1, textTransform: 'uppercase' }}>
                    {item.type}
                  </div>
                )}
                {isScene && pct > 5 && (
                  <div style={{ fontSize: 8, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>
                    {project.stories.find((s) => s.id === item.storyId)?.index ?? ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Shared UI helpers ────────────────────────────────────────

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#334155', fontSize: 13 }}>
    {text}
  </div>
);

const Spinner: React.FC = () => (
  <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>
      {label}
    </label>
    {children}
  </div>
);

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px',
  background: '#0f172a', border: '1px solid #1e293b',
  borderRadius: 8, color: '#e2e8f0', fontSize: 13,
  outline: 'none', boxSizing: 'border-box',
};

const primaryBtn: React.CSSProperties = {
  padding: '9px 20px', borderRadius: 8, border: 'none',
  cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 700,
};

const secondaryBtn: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 8,
  border: '1px solid #334155', background: 'transparent',
  cursor: 'pointer', color: '#94a3b8', fontSize: 13,
};

// ─── Render Overlay ───────────────────────────────────────────

interface RenderState {
  percent: number;
  stage: string;
  message: string;
  outputUrl?: string;
  error?: string;
}

const RenderOverlay: React.FC<{ jobId: string; onClose: () => void }> = ({ jobId, onClose }) => {
  const [state, setState] = useState<RenderState>({ percent: 0, stage: 'preparing', message: 'Starting render…' });
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const es = new EventSource(`/api/show-render/${jobId}/progress`);
    esRef.current = es;

    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data) as { percent: number; stage: string; message: string; outputUrl?: string };
        setState({ percent: data.percent, stage: data.stage, message: data.message, outputUrl: data.outputUrl });
        if (data.stage === 'completed' || data.stage === 'error') es.close();
      } catch { /* ignore parse errors */ }
    };

    es.onerror = () => {
      setState((s) => ({ ...s, error: 'Connection lost', stage: 'error' }));
      es.close();
    };

    return () => { es.close(); };
  }, [jobId]);

  const done = state.stage === 'completed';
  const failed = state.stage === 'error';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#0d1117', border: '1px solid #1e293b', borderRadius: 16,
        padding: 36, width: 420, maxWidth: '90vw',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <div style={{ fontSize: 24 }}>{done ? '✅' : failed ? '❌' : '🎬'}</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#e2e8f0' }}>
              {done ? 'Render Complete' : failed ? 'Render Failed' : 'Rendering Show…'}
            </div>
            <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>Job: {jobId.slice(0, 8)}…</div>
          </div>
        </div>

        {/* Progress bar */}
        {!done && !failed && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ height: 6, background: '#1e293b', borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
              <div style={{ height: '100%', width: `${state.percent}%`, background: '#3b82f6', borderRadius: 3, transition: 'width 0.3s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#475569' }}>
              <span>{state.message}</span>
              <span>{state.percent}%</span>
            </div>
          </div>
        )}

        {/* Stage list */}
        {!done && !failed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
            {[
              { id: 'preparing', label: 'Preparing' },
              { id: 'building-timeline', label: 'Bundling' },
              { id: 'rendering-frames', label: 'Rendering Scenes' },
              { id: 'encoding-video', label: 'Concatenating' },
              { id: 'exporting-mp4', label: 'Finalising' },
            ].map((s) => {
              const stages = ['preparing', 'building-timeline', 'rendering-frames', 'encoding-video', 'exporting-mp4'];
              const current = stages.indexOf(state.stage);
              const idx = stages.indexOf(s.id);
              const done = idx < current;
              const active = idx === current;
              return (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: done ? '#22c55e' : active ? '#3b82f6' : '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', flexShrink: 0 }}>
                    {done ? '✓' : active ? '…' : ''}
                  </div>
                  <span style={{ fontSize: 12, color: active ? '#e2e8f0' : done ? '#64748b' : '#334155' }}>{s.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Done state */}
        {done && state.outputUrl && (
          <div style={{ marginBottom: 20 }}>
            {state.message && state.message.includes('failed scene') ? (
              <div style={{ marginBottom: 12, padding: 10, background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 8, fontSize: 11, color: '#fbbf24', lineHeight: 1.5 }}>
                ⚠️ {state.message}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>Your show has been rendered successfully.</div>
            )}
            <a
              href={state.outputUrl}
              download
              style={{ ...primaryBtn, background: '#22c55e', display: 'block', textAlign: 'center', textDecoration: 'none', padding: '11px 20px', borderRadius: 8 }}
            >
              ⬇ Download MP4
            </a>
          </div>
        )}

        {/* Error state */}
        {failed && (
          <div style={{ marginBottom: 20, padding: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, fontSize: 12, color: '#f87171', lineHeight: 1.5 }}>
            {state.message || state.error || 'Unknown render error'}
          </div>
        )}

        <button onClick={onClose} style={secondaryBtn}>
          {done ? 'Close' : failed ? '← Close' : '✕ Cancel'}
        </button>
      </div>
    </div>
  );
};

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

// CSS animation for spinner
const style = document.createElement('style');
style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
document.head.appendChild(style);
